import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { X, ArrowDownRight, ArrowUpRight, ArrowRightLeft, ArrowRight, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import * as XLSX from 'xlsx';
import '../styles/KasaYonetimi.css';
import TrDateInput from '../components/TrDateInput';

interface Odeme {
  odemeID: number;
  cariID: number;
  islemTarihi: string;
  vadeTarihi?: string;
  tahsilatTarihi?: string;
  miktar: number;
  paraBirimiID: number;
  islemTakipNo: string;
  odemeTuru: string;
  odemeYonu: string;
  odemeDurumu: string;
  aciklama?: string;
  aktifMi: boolean;
  sozlesmeOdemesi: boolean;
  eklemeTarihi: string;
  projeID?: number | null;
  kasaID?: number;
  kasa?: {
    kasaID: number;
    kasaAdi: string;
    kasaTuru: string;
  };
  cari: {
    cariID: number;
    adiSoyadi: string;
    telefon: string;
    firmaAdi?: string;
  };
  paraBirimi: {
    paraBirimiID: number;
    birim: string;
    carpan: number;
  };
  sozlesme?: {
    sozlesmeID: number;
    sozlesmeTakipNo: string;
    projeID?: number;
    projeAdi?: string;
    daireNo?: string;
  };
  ilgiliProje?: {
    projeID: number;
    projeAdi: string;
  } | null;
  islemTipi?: string;
}

interface Proje {
  projeID: number;
  projeAdi: string;
  takipNo: string;
}

interface ParaBirimiToplam {
  birim: string;
  toplam: number;
}

interface KasaDashboardData {
  nakitKasalar: { kasaID: number; kasaAdi: string; kasaTuru: string; bakiye: number; paraBirimi: { paraBirimiID: number; birim: string } | null }[];
  bankaKasalar: { kasaID: number; kasaAdi: string; kasaTuru: string; bakiye: number; paraBirimi: { paraBirimiID: number; birim: string } | null }[];
  toplamNakit: number;
  toplamBanka: number;
  genelToplam: number;
  nakitParaBirimleri: ParaBirimiToplam[];
  bankaParaBirimleri: ParaBirimiToplam[];
  genelParaBirimleri: ParaBirimiToplam[];
}

interface Cari {
  cariID: number;
  adiSoyadi: string;
  turu?: string;
  firmaAdi?: string;
}

interface CariSozlesme {
  sozlesmeID: number;
  sozlesmeTakipNo: string;
  sozlesmeTarihi?: string;
  sozlesmeDurumu?: string;
  sozlesmeFiyati?: number;
  aktifMi?: boolean;
  daire?: {
    daireID: number;
    daireNo: string;
    blok?: {
      blokID?: number;
      blokAdi: string;
      proje?: { projeID: number; projeAdi: string; };
    };
  };
}

interface Kategori {
  kategoriID: number;
  kategoriAdi: string;
  tur: string;
}

interface GenelIslem {
  hareketID: number;
  kasaID: number;
  islemTuru: string;
  islemTipi?: string;
  miktar: number;
  tarih: string;
  aciklama?: string;
  kategori?: { kategoriID: number; kategoriAdi: string; tur: string; };
  kasa?: { kasaID: number; kasaAdi: string; kasaTuru: string; };
  proje?: { projeID: number; projeAdi: string; };
}

interface SozlesmePlanTaksit {
  taksitID: number;
  sozlesmeID: number;
  sozlesmeTakipNo?: string;
  cariID: number;
  vadeTarihi: string;
  miktar: number;
  odemeTuruDetay: string;
  siraNo: number;
}

interface AcikFatura {
  tip: 'odeme' | 'sozlesme';
  id: number;
  aciklama: string;
  islemTarihi: string;
  vadeTarihi?: string | null;
  odemeYonu: string;
  tutarToplam: number;
  odenenTutar: number;
  kalanTutar: number;
  durum: string;
}

const formatMoneyInput = (value: string): string => {
  const numbers = value.replace(/[^\d]/g, '');
  if (numbers === '') return '';
  return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseMoneyInput = (value: string): string => {
  return value.replace(/\./g, '');
};

const Odemeler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get('edit');
  
  const [odemeler, setOdemeler] = useState<Odeme[]>([]);
  const [filteredOdemeler, setFilteredOdemeler] = useState<Odeme[]>([]);
  const [projeler, setProjeler] = useState<Proje[]>([]);
  const [kasalar, setKasalar] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedOdeme, setSelectedOdeme] = useState<Odeme | null>(null);

  // Kasa dashboard state
  const [kasaDashboard, setKasaDashboard] = useState<KasaDashboardData | null>(null);
  const [kasaActiveTab, setKasaActiveTab] = useState<'ozet' | 'nakit' | 'banka'>('ozet');
  const [showGecikPopup, setShowGecikPopup] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Sayfalama state'leri
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);

  // Düzenleme modal state'leri
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingOdeme, setEditingOdeme] = useState<Odeme | null>(null);
  const [editDurum, setEditDurum] = useState<string>('');
  const [editKasaID, setEditKasaID] = useState<string>('');
  const [editMiktar, setEditMiktar] = useState<string>('');
  const [editTarih, setEditTarih] = useState<string>('');
  const [editTarihDisplay, setEditTarihDisplay] = useState<string>('');
  const [editOdemeTuru, setEditOdemeTuru] = useState<string>('Nakit');
  const [editAciklama, setEditAciklama] = useState<string>('');
  const [editSaving, setEditSaving] = useState<boolean>(false);

  // Yeni İşlem modal state'leri
  const [showIslemModal, setShowIslemModal] = useState<boolean>(false);
  const [showIslemSecimModal, setShowIslemSecimModal] = useState<boolean>(false);
  const [islemModu, setIslemModu] = useState<'gelir' | 'gider' | 'tahsilat' | 'odeme' | null>(null);
  const [wizardYon, setWizardYon] = useState<'gelir' | 'gider' | 'transfer' | null>(null);
  const [wizardTur, setWizardTur] = useState<'gelir' | 'gider' | 'tahsilat' | 'odeme' | null>(null);
  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferFormData, setTransferFormData] = useState({ kaynakKasaID: '', hedefKasaID: '', miktar: '', tarih: new Date().toISOString().split('T')[0], aciklama: '' });
  const [transferSaving, setTransferSaving] = useState(false);
  const [cariler, setCariler] = useState<Cari[]>([]);
  const [kategoriler, setKategoriler] = useState<Kategori[]>([]);
  const [genelIslemler, setGenelIslemler] = useState<GenelIslem[]>([]);

  // Cari arama state'leri (Cari İşlemi için)
  const [cariTipi, setCariTipi] = useState<'musteri' | 'tedarikci'>('musteri');
  const [cariArama, setCariArama] = useState<string>('');
  const [showCariDropdown, setShowCariDropdown] = useState<boolean>(false);
  const [cariSozlesmeleri, setCariSozlesmeleri] = useState<CariSozlesme[]>([]);

  // Tahsilat/Ödeme İşlemi modal state'leri
  const [showTahsilatOdemeModal, setShowTahsilatOdemeModal] = useState<boolean>(false);
  const [tahsilatOdemeModu, setTahsilatOdemeModu] = useState<'tahsilat' | 'odeme'>('tahsilat');
  const [tahsilatOdemeStep, setTahsilatOdemeStep] = useState<1 | 2 | 3>(1);
  const [tahsilatOdemeSelectedCari, setTahsilatOdemeSelectedCari] = useState<number | null>(null);
  const [tahsilatOdemeBekleyenler, setTahsilatOdemeBekleyenler] = useState<Odeme[]>([]);
  const [tahsilatOdemeTaksitler, setTahsilatOdemeTaksitler] = useState<Odeme[]>([]);
  const [tahsilatOdemeEditOdeme, setTahsilatOdemeEditOdeme] = useState<Odeme | null>(null);
  const [tahsilatOdemeEditDurum, setTahsilatOdemeEditDurum] = useState<string>('Bekliyor');
  const [tahsilatOdemeEditKasa, setTahsilatOdemeEditKasa] = useState<string>('');
  const [tahsilatOdemeCariArama, setTahsilatOdemeCariArama] = useState<string>('');
  const [tahsilatTedarikciGoster, setTahsilatTedarikciGoster] = useState<boolean>(false);
  const [cariBakiyeleri, setCariBakiyeleri] = useState<Record<number, number>>({});
  const [tahsilatOdemeKismiMi, setTahsilatOdemeKismiMi] = useState<boolean>(false);
  const [tahsilatOdemeKismiMiktar, setTahsilatOdemeKismiMiktar] = useState<string>('');
  const [islemFormData, setIslemFormData] = useState({
    kasaID: 0,
    kasaTuru: 'Nakit',
    islemTuru: 0,
    miktar: '',
    tarih: new Date().toISOString().split('T')[0],
    vadeTarihi: '',
    islemTipi: 'cari',
    cariID: '',
    projeID: '',
    kategoriID: '',
    aciklama: '',
    sozlesmeID: ''
  });
  const [tahsilatBicimi, setTahsilatBicimi] = useState<'vadeli' | 'vadesiz' | null>(null);

  // Bağımsız makbuz + allocation state'leri
  const [makbuzFormData, setMakbuzFormData] = useState({ miktar: '', tarih: '', kasaID: '', odemeTuru: 'Nakit', aciklama: '', sozlesmeID: '' });
  const [makbuzSaving, setMakbuzSaving] = useState(false);
  const [savedMakbuzOdemeID, setSavedMakbuzOdemeID] = useState<number | null>(null);
  const [acikFaturalar, setAcikFaturalar] = useState<AcikFatura[]>([]);
  const [eslesmeTutarlari, setEslesmeTutarlari] = useState<Record<string, string>>({});
  const [eslesmeSaving, setEslesmeSaving] = useState(false);

  // Filter state'leri
  const [filters, setFilters] = useState({
    tarihBaslangic: `${new Date().getFullYear()}-01-01`,
    tarihBitis: `${new Date().getFullYear()}-12-31`,
    odemeYonleri: ['Tahsilat', 'Odeme'] as string[],
    durumlar: [] as string[],
    durum: '' as string,
    musteriAdi: '',
    projeSecimi: 'tumü',
    islemTipi: '' as string,
    tur: '' as string,
    siralama: 'islemTarihi-desc' as string
  });
  const [dateDisplays, setDateDisplays] = useState({
    baslangic: `01.01.${new Date().getFullYear()}`,
    bitis: `31.12.${new Date().getFullYear()}`
  });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [formTarihDisplay, setFormTarihDisplay] = useState('');
  const [formVadeTarihDisplay, setFormVadeTarihDisplay] = useState('');
  const [makbuzTarihDisplay, setMakbuzTarihDisplay] = useState('');
  const [sozlesmePlanTaksitleri, setSozlesmePlanTaksitleri] = useState<SozlesmePlanTaksit[]>([]);
  const [secilenPlanTaksit, setSecilenPlanTaksit] = useState<SozlesmePlanTaksit | null>(null);
  const [taksitListAcik, setTaksitListAcik] = useState<boolean>(false);

  useEffect(() => {
    fetchOdemeler();
    fetchProjeler();
    fetchKasalar();
    fetchCariler();
    fetchKategoriler();
    fetchKasaDashboard();
    fetchGenelIslemler();
  }, []);

  useEffect(() => {
    if (editId && odemeler.length > 0) {
      const odeme = odemeler.find(o => o.odemeID === parseInt(editId));
      if (odeme) {
        setSelectedOdeme(odeme);
        toast.info(`${odeme.cari.adiSoyadi} - ${formatCurrency(odeme.miktar)} ${odeme.paraBirimi.birim} ödeme kaydı seçildi.`);
      }
    }
  }, [editId, odemeler]);

  const fetchOdemeler = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/Odemeler?aktifMi=true');
      const finansalIslemler = response.data.filter((o: Odeme) =>
        o.odemeTuru !== 'Urun' &&
        o.odemeTuru !== 'Hizmet'
      );
      setOdemeler(finansalIslemler);
      setFilteredOdemeler(finansalIslemler);
    } catch (error) {
      toast.error('Ödemeler yüklenirken bir hata oluştu!');
      console.error('Ödemeler yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjeler = async () => {
    try {
      const response = await axiosInstance.get('/api/Projeler');
      setProjeler(response.data);
    } catch (error) {
      console.error('Projeler yükleme hatası:', error);
    }
  };

  const fetchKasalar = async () => {
    try {
      const response = await axiosInstance.get('/api/Kasalar');
      setKasalar(response.data);
    } catch (error) {
      toast.error('Kasalar yüklenirken bir hata oluştu');
    }
  };

  const fetchKasaDashboard = async () => {
    try {
      const response = await axiosInstance.get('/api/KasaHareketleri/dashboard');
      setKasaDashboard(response.data);
    } catch (error) {
      console.error('Kasa dashboard yüklenirken hata:', error);
    }
  };

  const fetchCariler = async () => {
    try {
      const response = await axiosInstance.get('/api/Cariler?turler=M%C3%BCşteri,Cari,Tedarikçi');
      setCariler(response.data);
    } catch (error) {
      console.error('Cariler yüklenirken hata:', error);
    }
  };

  const fetchKategoriler = async () => {
    try {
      const response = await axiosInstance.get('/api/FinansKategorileri');
      setKategoriler(response.data);
    } catch (error) {
      console.error('Kategoriler yüklenirken hata:', error);
    }
  };

  const fetchGenelIslemler = async () => {
    try {
      const response = await axiosInstance.get('/api/KasaHareketleri/genel-islemler');
      setGenelIslemler(response.data);
    } catch (error) {
      console.error('Genel islemler yüklenirken hata:', error);
    }
  };

  const fetchCariSozlesmeleri = async (cariID: number) => {
    setCariSozlesmeleri([]);
    try {
      const response = await axiosInstance.get(`/api/Sozlesmeler?cariID=${cariID}&aktifMi=true`);
      setCariSozlesmeleri(response.data || []);
    } catch {
      setCariSozlesmeleri([]);
    }
  };

  const handleCariTipiChange = (tipi: 'musteri' | 'tedarikci') => {
    setCariTipi(tipi);
    setIslemFormData(prev => ({ ...prev, cariID: '', sozlesmeID: '' }));
    setCariArama('');
    setShowCariDropdown(false);
    setCariSozlesmeleri([]);
  };

  const handleCariSec = (cari: Cari) => {
    setIslemFormData(prev => ({ ...prev, cariID: String(cari.cariID), sozlesmeID: '' }));
    setCariArama('');
    setShowCariDropdown(false);
    setCariSozlesmeleri([]);
    if (cariTipi === 'musteri') {
      fetchCariSozlesmeleri(cari.cariID);
    }
  };

  const getCariAramaSonuclari = (): Cari[] => {
    if (cariArama.length < 3) return [];
    const aramaLower = cariArama.toLowerCase();
    const turFilter = cariTipi === 'musteri' ? 'Müşteri' : 'Cari';
    return cariler.filter(c =>
      c.turu === turFilter &&
      (c.adiSoyadi.toLowerCase().includes(aramaLower) ||
        (c.firmaAdi && c.firmaAdi.toLowerCase().includes(aramaLower)))
    );
  };

  const handleYeniIslem = () => {
    setWizardYon(null);
    setWizardTur(null);
    setShowIslemSecimModal(true);
  };

  const handleIslemSecim = (mod: 'gelir' | 'gider' | 'tahsilat' | 'odeme') => {
    if (mod === 'tahsilat' || mod === 'odeme') {
      setTahsilatOdemeModu(mod === 'tahsilat' ? 'tahsilat' : 'odeme');
      setTahsilatOdemeStep(1);
      setTahsilatOdemeSelectedCari(null);
      setTahsilatOdemeBekleyenler([]);
      setTahsilatOdemeTaksitler([]);
      setTahsilatOdemeEditOdeme(null);
      setTahsilatOdemeCariArama('');
      setTahsilatTedarikciGoster(false);
      setTahsilatTedarikciGoster(false);
      setCariSozlesmeleri([]);
      axiosInstance.get('/api/Cariler/bakiyeler').then(r => {
        const map: Record<number, number> = {};
        (r.data as { cariID: number; bakiye: number }[]).forEach(b => { map[b.cariID] = b.bakiye; });
        // Müşteri bakiyeleri için doğru endpoint'i de çek ve üzerine yaz
        axiosInstance.get('/api/Cariler/musteri-net-bakiyeler').then(r2 => {
          (r2.data as { cariID: number; netBakiye: number }[]).forEach(b => { map[b.cariID] = b.netBakiye; });
          setCariBakiyeleri({ ...map });
        }).catch(() => { setCariBakiyeleri(map); });
      }).catch(() => {});
      const todayIso = new Date().toISOString().split('T')[0];
      setMakbuzFormData({ miktar: '', tarih: todayIso, kasaID: '', odemeTuru: 'Nakit', aciklama: '', sozlesmeID: '' });
      setMakbuzTarihDisplay(isoToDisplay(todayIso));
      setSavedMakbuzOdemeID(null);
      setAcikFaturalar([]);
      setEslesmeTutarlari({});
      setSozlesmePlanTaksitleri([]);
      setSecilenPlanTaksit(null);
      setTaksitListAcik(false);
      setShowIslemSecimModal(false);
      setShowTahsilatOdemeModal(true);
      return;
    }
    setIslemModu(mod);
    const todayIso = new Date().toISOString().split('T')[0];
    setIslemFormData({
      kasaID: 0,
      kasaTuru: 'Nakit',
      islemTuru: mod === 'gider' ? 1 : 0,
      miktar: '',
      tarih: todayIso,
      vadeTarihi: '',
      islemTipi: 'cari',
      cariID: '',
      projeID: '',
      kategoriID: '',
      aciklama: '',
      sozlesmeID: ''
    });
    setFormTarihDisplay(isoToDisplay(todayIso));
    setFormVadeTarihDisplay('');
    setCariTipi('musteri');
    setCariArama('');
    setShowCariDropdown(false);
    setCariSozlesmeleri([]);
    setTahsilatBicimi(null);
    setShowIslemSecimModal(false);
    setShowIslemModal(true);
  };

  const handleOpenTransferModal = () => {
    setTransferFormData({ kaynakKasaID: '', hedefKasaID: '', miktar: '', tarih: new Date().toISOString().split('T')[0], aciklama: '' });
    setShowIslemSecimModal(false);
    setShowTransferModal(true);
  };

  const getKasaBakiye = (kasaID: number): number | null => {
    if (!kasaDashboard) return null;
    const all = [...(kasaDashboard.nakitKasalar || []), ...(kasaDashboard.bankaKasalar || [])];
    const found = all.find(k => k.kasaID === kasaID);
    return found ? found.bakiye : null;
  };

  const handleTransferSave = async () => {
    const { kaynakKasaID, hedefKasaID, miktar, tarih, aciklama } = transferFormData;
    if (!kaynakKasaID || !hedefKasaID) { toast.error('Lütfen kaynak ve hedef kasayı seçin!'); return; }
    if (kaynakKasaID === hedefKasaID) { toast.error('Kaynak ve hedef kasa aynı olamaz!'); return; }
    if (!miktar || parseFloat(parseMoneyInput(miktar)) <= 0) { toast.error('Geçerli bir miktar girin!'); return; }
    const kaynakKasa = kasalar.find(k => k.kasaID === parseInt(kaynakKasaID));
    const hedefKasa = kasalar.find(k => k.kasaID === parseInt(hedefKasaID));
    const transferAciklama = `${kaynakKasa?.kasaAdi || 'Kasa'} > ${hedefKasa?.kasaAdi || 'Kasa'} arası transfer${aciklama ? ' - ' + aciklama : ''}`;
    const miktarVal = parseFloat(parseMoneyInput(miktar));
    try {
      setTransferSaving(true);
      await axiosInstance.post('/api/KasaHareketleri', {
        kasaID: parseInt(kaynakKasaID),
        islemTuru: 1, // Çıkış
        miktar: miktarVal,
        tarih,
        aciklama: transferAciklama,
        islemTipi: 4, cariID: null, projeID: null, kategoriID: null
      });
      await axiosInstance.post('/api/KasaHareketleri', {
        kasaID: parseInt(hedefKasaID),
        islemTuru: 0, // Giriş
        miktar: miktarVal,
        tarih,
        aciklama: transferAciklama,
        islemTipi: 4, cariID: null, projeID: null, kategoriID: null
      });
      toast.success('Transfer başarıyla kaydedildi!');
      setShowTransferModal(false);
      fetchGenelIslemler();
      fetchKasaDashboard();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Transfer kaydedilemedi!');
      console.error('Transfer hatası:', error);
    } finally {
      setTransferSaving(false);
    }
  };

  const handleIslemFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Gelir İşlemi — yeni akış (vadeli / vadesiz)
    if (islemModu === 'gelir') {
      if (!tahsilatBicimi) {
        toast.error('Lütfen tahsilat biçimi seçin');
        return;
      }
      if (!islemFormData.miktar || parseFloat(parseMoneyInput(islemFormData.miktar)) <= 0) {
        toast.error('Geçerli bir miktar girin');
        return;
      }

      if (tahsilatBicimi === 'vadeli') {
        if (!islemFormData.cariID) {
          toast.error('Lütfen bir cari seçin');
          return;
        }
        try {
          const kategori = islemFormData.kategoriID
            ? kategoriler.find(k => k.kategoriID === parseInt(islemFormData.kategoriID))
            : null;
          const aciklama = [kategori ? `[${kategori.kategoriAdi}]` : null, islemFormData.aciklama || null].filter(Boolean).join(' ') || null;
          await axiosInstance.post('/api/Odemeler', {
            cariID: parseInt(islemFormData.cariID),
            islemTarihi: islemFormData.tarih,
            vadeTarihi: islemFormData.tarih,
            miktar: parseFloat(parseMoneyInput(islemFormData.miktar)),
            paraBirimiID: 1,
            islemTakipNo: `GEL-${Date.now()}`,
            odemeTuru: 'Nakit',
            odemeYonu: 'Tahsilat',
            odemeDurumu: 'Bekliyor',
            islemTipi: 0,
            aciklama,
            aktifMi: true,
            sozlesmeOdemesi: false,
            projeID: islemFormData.projeID ? parseInt(islemFormData.projeID) : null
          });
          toast.success('Vadeli gelir kaydı oluşturuldu!');
          setShowIslemModal(false);
          fetchOdemeler();
        } catch (error: any) {
          const errData = error.response?.data;
          const errorMessage = errData?.detail
            ? `${errData.message}: ${errData.detail}`
            : (errData?.error || errData?.message || 'İşlem kaydedilemedi');
          toast.error(errorMessage, { autoClose: 10000 });
        }
        return;
      }

      if (tahsilatBicimi === 'vadesiz') {
        if (!islemFormData.kasaID) {
          toast.error('Lütfen bir kasa seçin');
          return;
        }
        if (islemFormData.kasaTuru === 'Banka' && !islemFormData.aciklama) {
          toast.error('Banka işlemleri için açıklama zorunludur');
          return;
        }
        try {
          const kategori = islemFormData.kategoriID
            ? kategoriler.find(k => k.kategoriID === parseInt(islemFormData.kategoriID))
            : null;
          const aciklama = [kategori ? `[${kategori.kategoriAdi}]` : null, islemFormData.aciklama || null].filter(Boolean).join(' ') || null;
          await axiosInstance.post('/api/Odemeler', {
            cariID: islemFormData.cariID ? parseInt(islemFormData.cariID) : null,
            islemTarihi: islemFormData.tarih,
            vadeTarihi: islemFormData.tarih,
            miktar: parseFloat(parseMoneyInput(islemFormData.miktar)),
            paraBirimiID: 1,
            islemTakipNo: `GEL-${Date.now()}`,
            odemeTuru: 'Nakit',
            odemeYonu: 'Tahsilat',
            odemeDurumu: 'Odendi',
            islemTipi: 0,
            aciklama,
            aktifMi: true,
            sozlesmeOdemesi: false,
            kasaID: islemFormData.kasaID,
            projeID: islemFormData.projeID ? parseInt(islemFormData.projeID) : null
          });
          toast.success('Gelir başarıyla kaydedildi!');
          setShowIslemModal(false);
          fetchOdemeler();
          fetchKasaDashboard();
        } catch (error: any) {
          const errData = error.response?.data;
          const errorMessage = errData?.detail
            ? `${errData.message}: ${errData.detail}`
            : (errData?.error || errData?.message || 'İşlem kaydedilemedi');
          toast.error(errorMessage, { autoClose: 10000 });
        }
        return;
      }
      return;
    }

    // Gider İşlemi — vadeli / vadesiz
    if (islemModu === 'gider') {
      if (!tahsilatBicimi) {
        toast.error('Lütfen ödeme biçimi seçin');
        return;
      }
      if (!islemFormData.miktar || parseFloat(parseMoneyInput(islemFormData.miktar)) <= 0) {
        toast.error('Geçerli bir miktar girin');
        return;
      }

      if (tahsilatBicimi === 'vadeli') {
        if (!islemFormData.cariID) {
          toast.error('Lütfen bir cari seçin');
          return;
        }
        if (!islemFormData.vadeTarihi) {
          toast.error('Lütfen vade tarihi girin');
          return;
        }
        try {
          const kategori = islemFormData.kategoriID
            ? kategoriler.find(k => k.kategoriID === parseInt(islemFormData.kategoriID))
            : null;
          const aciklama = [kategori ? `[${kategori.kategoriAdi}]` : null, islemFormData.aciklama || null].filter(Boolean).join(' ') || null;
          await axiosInstance.post('/api/Odemeler', {
            cariID: parseInt(islemFormData.cariID),
            islemTarihi: islemFormData.tarih,
            vadeTarihi: islemFormData.vadeTarihi,
            miktar: parseFloat(parseMoneyInput(islemFormData.miktar)),
            paraBirimiID: 1,
            islemTakipNo: `GID-${Date.now()}`,
            odemeTuru: 0,
            odemeYonu: 'Odeme',
            odemeDurumu: 'Bekliyor',
            islemTipi: 1,
            aciklama,
            aktifMi: true,
            sozlesmeOdemesi: false,
            projeID: islemFormData.projeID ? parseInt(islemFormData.projeID) : null
          });
          toast.success('Vadeli gider kaydı oluşturuldu!');
          setShowIslemModal(false);
          fetchOdemeler();
        } catch (error: any) {
          const errData = error.response?.data;
          const errorMessage = errData?.detail
            ? `${errData.message}: ${errData.detail}`
            : (errData?.error || errData?.message || 'İşlem kaydedilemedi');
          toast.error(errorMessage, { autoClose: 10000 });
        }
        return;
      }

      if (tahsilatBicimi === 'vadesiz') {
        if (!islemFormData.kasaID) {
          toast.error('Lütfen bir kasa seçin');
          return;
        }
        if (islemFormData.kasaTuru === 'Banka' && !islemFormData.aciklama) {
          toast.error('Banka işlemleri için açıklama zorunludur');
          return;
        }
        try {
          const kategori = islemFormData.kategoriID
            ? kategoriler.find(k => k.kategoriID === parseInt(islemFormData.kategoriID))
            : null;
          const aciklama = [kategori ? `[${kategori.kategoriAdi}]` : null, islemFormData.aciklama || null].filter(Boolean).join(' ') || null;
          await axiosInstance.post('/api/Odemeler', {
            cariID: islemFormData.cariID ? parseInt(islemFormData.cariID) : null,
            islemTarihi: islemFormData.tarih,
            vadeTarihi: islemFormData.tarih,
            miktar: parseFloat(parseMoneyInput(islemFormData.miktar)),
            paraBirimiID: 1,
            islemTakipNo: `GID-${Date.now()}`,
            odemeTuru: 'Nakit',
            odemeYonu: 'Odeme',
            odemeDurumu: 'Odendi',
            islemTipi: 1,
            aciklama,
            aktifMi: true,
            sozlesmeOdemesi: false,
            kasaID: islemFormData.kasaID,
            projeID: islemFormData.projeID ? parseInt(islemFormData.projeID) : null
          });
          toast.success('Gider başarıyla kaydedildi!');
          setShowIslemModal(false);
          fetchOdemeler();
          fetchKasaDashboard();
        } catch (error: any) {
          const errData = error.response?.data;
          const errorMessage = errData?.detail
            ? `${errData.message}: ${errData.detail}`
            : (errData?.error || errData?.message || 'İşlem kaydedilemedi');
          toast.error(errorMessage, { autoClose: 10000 });
        }
        return;
      }
      return;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  const isoToDisplay = (iso: string): string => {
    if (!iso || iso.length !== 10) return iso;
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  };

  const handleDateDisplayChange = (field: 'baslangic' | 'bitis', value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 2) formatted = digits.slice(0, 2) + '.' + digits.slice(2);
    if (digits.length > 4) formatted = digits.slice(0, 2) + '.' + digits.slice(2, 4) + '.' + digits.slice(4);
    setDateDisplays(prev => ({ ...prev, [field]: formatted }));
    if (digits.length === 8) {
      const iso = `${digits.slice(4)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
      handleFilterChange(field === 'baslangic' ? 'tarihBaslangic' : 'tarihBitis', iso);
    }
  };

  // Filter fonksiyonları
  const applyFilters = () => {
    let filtered = [...odemeler];

    // Tarih filtresi
    if (filters.tarihBaslangic) {
      filtered = filtered.filter(o => {
        const d = o.vadeTarihi || o.islemTarihi;
        return !d || new Date(d) >= new Date(filters.tarihBaslangic);
      });
    }
    if (filters.tarihBitis) {
      const bitis = new Date(filters.tarihBitis);
      bitis.setHours(23, 59, 59, 999);
      filtered = filtered.filter(o => {
        const d = o.vadeTarihi || o.islemTarihi;
        return !d || new Date(d) <= bitis;
      });
    }

    // İşlem tipi filtresi
    if (filters.islemTipi) {
      if (filters.islemTipi === 'tahsilat') {
        filtered = filtered.filter(o => o.islemTipi === 'Tahsilat');
      } else if (filters.islemTipi === 'odeme') {
        filtered = filtered.filter(o => o.islemTipi === 'Odeme');
      } else if (filters.islemTipi === 'virman') {
        filtered = filtered.filter(o => o.islemTipi === 'Virman');
      }
    }

    // Tür filtresi (Gelir/Gider) — Gelir=Gelir+Tahsilat, Gider=Gider+Odeme
    if (filters.tur) {
      if (filters.tur === 'Gelir') {
        filtered = filtered.filter(o => o.islemTipi === 'Gelir' || o.islemTipi === 'Tahsilat');
      } else if (filters.tur === 'Gider') {
        filtered = filtered.filter(o => o.islemTipi === 'Gider' || o.islemTipi === 'Odeme');
      } else {
        filtered = filtered.filter(o => o.islemTipi === filters.tur);
      }
    }

    // Durum filtresi — boş array = Tümü (filtre yok)
    if (filters.durumlar.length > 0) {
      filtered = filtered.filter(odeme => filters.durumlar.includes(odeme.odemeDurumu));
    }
    if (filters.durum !== '') {
      filtered = filtered.filter(odeme => odeme.odemeDurumu === filters.durum);
    }

    // Müşteri adı filtresi
    if (filters.musteriAdi.trim()) {
      filtered = filtered.filter(odeme => 
        odeme.cari.adiSoyadi.toLowerCase().includes(filters.musteriAdi.toLowerCase()) ||
        (odeme.cari.firmaAdi && odeme.cari.firmaAdi.toLowerCase().includes(filters.musteriAdi.toLowerCase()))
      );
    }

    // Proje filtresi
    if (filters.projeSecimi !== 'tumü') {
      if (filters.projeSecimi === 'proje-disi') {
        // Proje dışı ödemeler: sozlesme bilgisi olmayan
        filtered = filtered.filter(odeme => !odeme.sozlesme);
      } else {
        // Belirli bir proje seçilmiş: ödemenin sözleşmesinin projesi ile seçilen proje eşleşmeli
        filtered = filtered.filter(odeme => {
          if (!odeme.sozlesme) return false;
          
          // Seçilen proje ID'si
          const secilenProjeID = parseInt(filters.projeSecimi);
          if (isNaN(secilenProjeID)) return false;
          

          
          // Eğer projeID varsa ID ile karşılaştır, yoksa proje adı ile karşılaştır
          if (odeme.sozlesme.projeID) {
            return odeme.sozlesme.projeID === secilenProjeID;
          } else if (odeme.sozlesme.projeAdi) {
            // ProjeID yoksa proje adı ile karşılaştır
            const secilenProje = projeler.find(p => p.projeID === secilenProjeID);
            return secilenProje && odeme.sozlesme.projeAdi === secilenProje.projeAdi;
          }
          
          return false;
        });
      }
    }

    setFilteredOdemeler(filtered);
  };

  // Widget hesaplama fonksiyonları
  const calculateToplamOdeme = (): number => {
    return filteredOdemeler.reduce((total, odeme) => total + odeme.miktar, 0);
  };

  const calculateBekleyenOdemeler = (): number => {
    return filteredOdemeler
      .filter(odeme => odeme.odemeDurumu === 'Bekliyor' && odeme.odemeYonu === 'Odeme')
      .reduce((total, odeme) => total + odeme.miktar, 0);
  };

  const calculateBekleyenTahsilatlar = (): number => {
    return filteredOdemeler
      .filter(odeme => odeme.odemeDurumu === 'Bekliyor' && odeme.odemeYonu === 'Tahsilat')
      .reduce((total, odeme) => total + odeme.miktar, 0);
  };

  const calculateGecenOdemeler = (): number => {
    const bugun = new Date();
    bugun.setHours(23, 59, 59, 999);
    
    return filteredOdemeler
      .filter(odeme => {
        if (odeme.odemeDurumu !== 'Bekliyor' || odeme.odemeYonu !== 'Odeme' || !odeme.vadeTarihi) return false;
        const vadeTarihi = new Date(odeme.vadeTarihi);
        return vadeTarihi < bugun;
      })
      .reduce((total, odeme) => total + odeme.miktar, 0);
  };

  const calculateGecenTahsilatlar = (): number => {
    const bugun = new Date();
    bugun.setHours(23, 59, 59, 999);
    
    return filteredOdemeler
      .filter(odeme => {
        if (odeme.odemeDurumu !== 'Bekliyor' || odeme.odemeYonu !== 'Tahsilat' || !odeme.vadeTarihi) return false;
        const vadeTarihi = new Date(odeme.vadeTarihi);
        return vadeTarihi < bugun;
      })
      .reduce((total, odeme) => total + odeme.miktar, 0);
  };

  const clearFilters = () => {
    const y = new Date().getFullYear();
    setFilters({
      tarihBaslangic: `${y}-01-01`,
      tarihBitis: `${y}-12-31`,
      odemeYonleri: ['Tahsilat', 'Odeme'],
      durumlar: [],
      durum: '',
      musteriAdi: '',
      projeSecimi: 'tumü',
      islemTipi: '',
      tur: '',
      siralama: 'islemTarihi-desc'
    });
    setDateDisplays({ baslangic: `01.01.${y}`, bitis: `31.12.${y}` });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCheckboxChange = (key: 'odemeYonleri' | 'durumlar', value: string) => {
    setFilters(prev => {
      const currentArray = prev[key];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      
      return {
        ...prev,
        [key]: newArray
      };
    });
  };

  // Filter'lar değiştiğinde otomatik uygula
  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Filtre değiştiğinde ilk sayfaya dön
  }, [filters, odemeler]);

  const handleSozlesmeDetayGit = (islemTakipNo: string) => {
    navigate(`/sozlesme-detay/${islemTakipNo}`);
  };

  const handleMusteriDetayGit = (cariID: number) => {
    navigate(`/musteriler/${cariID}`);
  };

  // Düzenleme fonksiyonları
  const handleDuzenleClick = (odeme: Odeme) => {
    setEditingOdeme(odeme);
    setEditDurum(odeme.odemeDurumu);
    setEditKasaID(odeme.kasaID ? odeme.kasaID.toString() : '');
    // Tutar
    setEditMiktar(odeme.miktar.toLocaleString('tr-TR'));
    // Tarih
    const tarihIso = odeme.islemTarihi ? odeme.islemTarihi.split('T')[0] : new Date().toISOString().split('T')[0];
    setEditTarih(tarihIso);
    setEditTarihDisplay(isoToDisplay(tarihIso));
    // Ödeme türü (string olarak normalize et)
    const odemeTuruMap: Record<string, string> = { '0': 'Nakit', '1': 'BankaHavale', '2': 'Cek', '3': 'Senet', '4': 'Takas', '5': 'Mahsup', Nakit: 'Nakit', BankaHavale: 'BankaHavale', Cek: 'Cek', Senet: 'Senet', Takas: 'Takas', Mahsup: 'Mahsup' };
    setEditOdemeTuru(odemeTuruMap[String(odeme.odemeTuru)] ?? 'Nakit');
    setEditAciklama(odeme.aciklama || '');
    setEditSaving(false);
    setShowEditModal(true);
  };

  const handleIptalEt = async () => {
    if (!editingOdeme) return;
    try {
      await axiosInstance.put(`/api/Odemeler/${editingOdeme.odemeID}`, {
        odemeID: editingOdeme.odemeID,
        cariID: editingOdeme.cariID,
        islemTarihi: editingOdeme.islemTarihi,
        vadeTarihi: editingOdeme.vadeTarihi,
        tahsilatTarihi: null,
        miktar: editingOdeme.miktar,
        paraBirimiID: editingOdeme.paraBirimiID,
        islemTakipNo: editingOdeme.islemTakipNo,
        odemeTuru: editingOdeme.odemeTuru,
        odemeYonu: editingOdeme.odemeYonu,
        odemeDurumu: 'Bekliyor',
        kasaID: null,
        aciklama: editingOdeme.aciklama,
        projeID: editingOdeme.projeID,
        aktifMi: editingOdeme.aktifMi,
        sozlesmeOdemesi: editingOdeme.sozlesmeOdemesi,
        eklemeTarihi: editingOdeme.eklemeTarihi
      });
      await fetchOdemeler();
      setShowEditModal(false);
      toast.success(editingOdeme.odemeYonu === 'Odeme' ? 'Ödeme iptal edildi!' : 'Tahsilat iptal edildi!');
    } catch {
      toast.error('İptal işlemi sırasında hata oluştu.');
    }
  };

  const handleDurumKaydet = async () => {
    if (!editingOdeme) return;
    if (!editKasaID) { toast.error('Lütfen kasa seçin!'); return; }
    try {
      const odemeData = {
        odemeID: editingOdeme.odemeID,
        cariID: editingOdeme.cariID,
        islemTarihi: editingOdeme.islemTarihi,
        vadeTarihi: editingOdeme.vadeTarihi,
        tahsilatTarihi: editingOdeme.tahsilatTarihi,
        miktar: editingOdeme.miktar,
        paraBirimiID: editingOdeme.paraBirimiID,
        islemTakipNo: editingOdeme.islemTakipNo,
        odemeTuru: editingOdeme.odemeTuru,
        odemeYonu: editingOdeme.odemeYonu,
        odemeDurumu: 'Odendi',
        kasaID: parseInt(editKasaID),
        aciklama: editingOdeme.aciklama,
        projeID: editingOdeme.projeID,
        aktifMi: editingOdeme.aktifMi,
        sozlesmeOdemesi: editingOdeme.sozlesmeOdemesi,
        eklemeTarihi: editingOdeme.eklemeTarihi
      };
      await axiosInstance.put(`/api/Odemeler/${editingOdeme.odemeID}`, odemeData);
      await fetchOdemeler();
      setShowEditModal(false);
      toast.success('Ödeme durumu başarıyla güncellendi!');
    } catch (error) {
      toast.error('Durum güncellenirken bir hata oluştu.');
    }
  };

  const handleDuzenlemeKaydet = async () => {
    if (!editingOdeme) return;
    const miktar = parseFloat(parseMoneyInput(editMiktar));
    if (!miktar || miktar <= 0) { toast.error('Geçerli bir tutar girin.'); return; }
    if (!editTarih) { toast.error('Tarih girin.'); return; }
    if (!editKasaID && editingOdeme.odemeDurumu === 'Odendi') { toast.error('Kasa seçin.'); return; }
    const odemeTuruNum = ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 } as Record<string, number>)[editOdemeTuru] ?? 0;
    try {
      setEditSaving(true);
      await axiosInstance.put(`/api/Odemeler/${editingOdeme.odemeID}`, {
        odemeID: editingOdeme.odemeID,
        cariID: editingOdeme.cariID,
        islemTarihi: editTarih,
        vadeTarihi: editingOdeme.vadeTarihi,
        tahsilatTarihi: editingOdeme.tahsilatTarihi,
        miktar,
        paraBirimiID: editingOdeme.paraBirimiID ?? 1,
        islemTakipNo: editingOdeme.islemTakipNo,
        odemeTuru: odemeTuruNum,
        odemeYonu: editingOdeme.odemeYonu,
        islemTipi: editingOdeme.islemTipi,
        odemeDurumu: editingOdeme.odemeDurumu,
        kasaID: editKasaID ? parseInt(editKasaID) : null,
        aciklama: editAciklama || null,
        projeID: editingOdeme.projeID,
        aktifMi: editingOdeme.aktifMi,
        sozlesmeOdemesi: editingOdeme.sozlesmeOdemesi,
        eklemeTarihi: editingOdeme.eklemeTarihi
      });
      await fetchOdemeler();
      fetchKasaDashboard();
      setShowEditModal(false);
      toast.success('Kayıt güncellendi!');
    } catch {
      toast.error('Güncelleme sırasında hata oluştu.');
    } finally {
      setEditSaving(false);
    }
  };

  // Tahsilat/Ödeme: Bekleyen ödemeleri olan müşteri listesi
  const getTahsilatOdemeBekleyenCariler = () => {
    const yon = tahsilatOdemeModu === 'tahsilat' ? 'Tahsilat' : 'Odeme';
    const bekleyenler = odemeler.filter(o => o.odemeDurumu === 'Bekliyor' && o.odemeYonu === yon && o.aktifMi);
    const cariMap = new Map<number, { bekleyenAdet: number; bekleyenToplam: number }>();
    bekleyenler.forEach(o => {
      const existing = cariMap.get(o.cariID);
      if (existing) {
        existing.bekleyenAdet += 1;
        existing.bekleyenToplam += o.miktar;
      } else {
        cariMap.set(o.cariID, { bekleyenAdet: 1, bekleyenToplam: o.miktar });
      }
    });
    let result = cariler
      .filter(c => {
        if (tahsilatOdemeModu === 'tahsilat') {
          // Tahsilat: varsayılan Müşteri, butonla Cari (tedarikçi) de eklenir
          return tahsilatTedarikciGoster || c.turu === 'Müşteri';
        } else {
          // Ödeme: varsayılan Cari (tedarikçi), butonla Müşteri de eklenir
          return tahsilatTedarikciGoster || c.turu === 'Cari';
        }
      })
      .map(c => ({
        cariID: c.cariID,
        adiSoyadi: c.adiSoyadi,
        firmaAdi: c.firmaAdi,
        turu: c.turu,
        bekleyenAdet: cariMap.get(c.cariID)?.bekleyenAdet ?? 0,
        bekleyenToplam: cariMap.get(c.cariID)?.bekleyenToplam ?? 0,
        ekstreBakiye: cariBakiyeleri[c.cariID],
      }));
    if (tahsilatOdemeCariArama.trim()) {
      const arama = tahsilatOdemeCariArama.toLowerCase().trim();
      result = result.filter(c => c.adiSoyadi.toLowerCase().includes(arama) || (c.firmaAdi && c.firmaAdi.toLowerCase().includes(arama)));
    }
    return result;
  };

  // Tahsilat/Ödeme: Müşteri seçildiğinde bekleyen ödemelerini yükle
  const handleTahsilatOdemeCariSec = async (cariID: number) => {
    setTahsilatOdemeSelectedCari(cariID);
    const yon = tahsilatOdemeModu === 'tahsilat' ? 'Tahsilat' : 'Odeme';
    const bekleyenler = odemeler
      .filter(o => o.cariID === cariID && o.odemeDurumu === 'Bekliyor' && o.odemeYonu === yon && o.aktifMi)
      .sort((a, b) => {
        if (!a.vadeTarihi && !b.vadeTarihi) return 0;
        if (!a.vadeTarihi) return 1;
        if (!b.vadeTarihi) return -1;
        return new Date(a.vadeTarihi).getTime() - new Date(b.vadeTarihi).getTime();
      });
    setTahsilatOdemeBekleyenler(bekleyenler);
    try {
      const resp = await axiosInstance.get(`/api/Odemeler?aktifMi=true&cariID=${cariID}`);
      const taksitler = (resp.data as Odeme[])
        .filter(o => o.aciklama && /^\d+\.\s*taksit/i.test(o.aciklama) && o.odemeDurumu === 'Bekliyor' && o.odemeYonu === yon && o.aktifMi)
        .sort((a, b) => {
          if (!a.vadeTarihi && !b.vadeTarihi) return 0;
          if (!a.vadeTarihi) return 1;
          if (!b.vadeTarihi) return -1;
          return new Date(a.vadeTarihi).getTime() - new Date(b.vadeTarihi).getTime();
        });
      setTahsilatOdemeTaksitler(taksitler);
    } catch {
      setTahsilatOdemeTaksitler([]);
    }
    setTahsilatOdemeEditOdeme(null);
    setTahsilatOdemeStep(2);
    fetchCariSozlesmeleri(cariID);
  };

  // Tahsilat/Ödeme: Ödeme düzenleme seç
  const handleTahsilatOdemeEditSec = (odeme: Odeme) => {
    setTahsilatOdemeEditOdeme(odeme);
    setTahsilatOdemeEditDurum(odeme.odemeDurumu);
    setTahsilatOdemeEditKasa(odeme.kasaID ? odeme.kasaID.toString() : '');
  };

  // Tahsilat/Ödeme: Durum kaydet
  const handleTahsilatOdemeDurumKaydet = async () => {
    if (!tahsilatOdemeEditOdeme) return;

    // Kısmi ödeme modu
    if (tahsilatOdemeKismiMi) {
      if (!tahsilatOdemeEditKasa) {
        toast.error('Kısmi ödeme için kasa seçimi zorunludur!');
        return;
      }
      const kismiTutar = parseFloat(parseMoneyInput(tahsilatOdemeKismiMiktar));
      if (!kismiTutar || isNaN(kismiTutar) || kismiTutar <= 0) {
        toast.error('Geçerli bir kısmi tutar girin!');
        return;
      }
      if (kismiTutar >= tahsilatOdemeEditOdeme.miktar) {
        toast.error('Kısmi tutar, toplam tutardan küçük olmalıdır. Tam ödeme için kısmi modu kapatın.');
        return;
      }
      const kalanTutar = tahsilatOdemeEditOdeme.miktar - kismiTutar;
      try {
        // 1) Orijinal kaydı kalan tutara düşür, Bekliyor'da bırak
        await axiosInstance.put(`/api/Odemeler/${tahsilatOdemeEditOdeme.odemeID}`, {
          odemeID: tahsilatOdemeEditOdeme.odemeID,
          cariID: tahsilatOdemeEditOdeme.cariID,
          islemTarihi: tahsilatOdemeEditOdeme.islemTarihi,
          vadeTarihi: tahsilatOdemeEditOdeme.vadeTarihi,
          tahsilatTarihi: tahsilatOdemeEditOdeme.tahsilatTarihi,
          miktar: kalanTutar,
          paraBirimiID: tahsilatOdemeEditOdeme.paraBirimiID,
          islemTakipNo: tahsilatOdemeEditOdeme.islemTakipNo,
          odemeTuru: tahsilatOdemeEditOdeme.odemeTuru,
          odemeYonu: tahsilatOdemeEditOdeme.odemeYonu,
          odemeDurumu: 'Bekliyor',
          kasaID: tahsilatOdemeEditOdeme.kasaID ?? null,
          aciklama: tahsilatOdemeEditOdeme.aciklama,
          projeID: tahsilatOdemeEditOdeme.projeID,
          aktifMi: tahsilatOdemeEditOdeme.aktifMi,
          sozlesmeOdemesi: tahsilatOdemeEditOdeme.sozlesmeOdemesi,
          eklemeTarihi: tahsilatOdemeEditOdeme.eklemeTarihi
        });
        // 2) Kısmi ödenen tutar için yeni "Odendi" kaydı oluştur
        await axiosInstance.post('/api/Odemeler', {
          cariID: tahsilatOdemeEditOdeme.cariID,
          islemTarihi: new Date().toISOString().split('T')[0],
          vadeTarihi: tahsilatOdemeEditOdeme.vadeTarihi,
          miktar: kismiTutar,
          paraBirimiID: tahsilatOdemeEditOdeme.paraBirimiID,
          islemTakipNo: `KP-${Date.now()}`,
          odemeTuru: tahsilatOdemeEditOdeme.odemeTuru,
          odemeYonu: tahsilatOdemeEditOdeme.odemeYonu,
          odemeDurumu: 'Odendi',
          kasaID: parseInt(tahsilatOdemeEditKasa),
          aciklama: `Kısmi ${tahsilatOdemeModu === 'tahsilat' ? 'tahsilat' : 'ödeme'}${tahsilatOdemeEditOdeme.aciklama ? ' - ' + tahsilatOdemeEditOdeme.aciklama : ''}`,
          projeID: tahsilatOdemeEditOdeme.projeID,
          aktifMi: true,
          sozlesmeOdemesi: tahsilatOdemeEditOdeme.sozlesmeOdemesi
        });
        await fetchOdemeler();
        // Güncellenen listeyi yenile
        const yon = tahsilatOdemeModu === 'tahsilat' ? 'Tahsilat' : 'Odeme';
        const cariID = tahsilatOdemeEditOdeme.cariID;
        setTahsilatOdemeBekleyenler(prev =>
          prev.map(o =>
            o.odemeID === tahsilatOdemeEditOdeme.odemeID
              ? { ...o, miktar: kalanTutar }
              : o
          )
        );
        setTahsilatOdemeTaksitler(prev =>
          prev.map(o =>
            o.odemeID === tahsilatOdemeEditOdeme.odemeID
              ? { ...o, miktar: kalanTutar }
              : o
          )
        );
        setTahsilatOdemeEditOdeme(null);
        setTahsilatOdemeKismiMi(false);
        setTahsilatOdemeKismiMiktar('');
        toast.success(`Kısmi ${tahsilatOdemeModu === 'tahsilat' ? 'tahsilat' : 'ödeme'} kaydedildi! Kalan tutar: ${kalanTutar.toLocaleString('tr-TR')} ₺`);
      } catch (error) {
        console.error('Kısmi ödeme kaydedilirken hata:', error);
        toast.error('Kısmi ödeme kaydedilemedi.');
      }
      return;
    }

    // Tam ödeme modu
    if (tahsilatOdemeEditDurum === 'Odendi' && !tahsilatOdemeEditKasa) {
      toast.error('Ödenmiş ödemeler için kasa seçimi zorunludur!');
      return;
    }

    try {
      const odemeData = {
        odemeID: tahsilatOdemeEditOdeme.odemeID,
        cariID: tahsilatOdemeEditOdeme.cariID,
        islemTarihi: tahsilatOdemeEditOdeme.islemTarihi,
        vadeTarihi: tahsilatOdemeEditOdeme.vadeTarihi,
        tahsilatTarihi: tahsilatOdemeEditOdeme.tahsilatTarihi,
        miktar: tahsilatOdemeEditOdeme.miktar,
        paraBirimiID: tahsilatOdemeEditOdeme.paraBirimiID,
        islemTakipNo: tahsilatOdemeEditOdeme.islemTakipNo,
        odemeTuru: tahsilatOdemeEditOdeme.odemeTuru,
        odemeYonu: tahsilatOdemeEditOdeme.odemeYonu,
        odemeDurumu: tahsilatOdemeEditDurum,
        kasaID: tahsilatOdemeEditKasa ? parseInt(tahsilatOdemeEditKasa) : null,
        aciklama: tahsilatOdemeEditOdeme.aciklama,
        projeID: tahsilatOdemeEditOdeme.projeID,
        aktifMi: tahsilatOdemeEditOdeme.aktifMi,
        sozlesmeOdemesi: tahsilatOdemeEditOdeme.sozlesmeOdemesi,
        eklemeTarihi: tahsilatOdemeEditOdeme.eklemeTarihi
      };

      await axiosInstance.put(`/api/Odemeler/${tahsilatOdemeEditOdeme.odemeID}`, odemeData);
      await fetchOdemeler();

      // Listeyi güncelle - düzenlenen ödemeyi bekleyenlerden çıkar
      if (tahsilatOdemeEditDurum !== 'Bekliyor') {
        setTahsilatOdemeBekleyenler(prev => prev.filter(o => o.odemeID !== tahsilatOdemeEditOdeme.odemeID));
        setTahsilatOdemeTaksitler(prev => prev.filter(o => o.odemeID !== tahsilatOdemeEditOdeme.odemeID));
      }
      setTahsilatOdemeEditOdeme(null);
      toast.success('Ödeme durumu başarıyla güncellendi!');
    } catch (error) {
      console.error('Durum güncellenirken hata oluştu:', error);
      toast.error('Durum güncellenirken bir hata oluştu.');
    }
  };

  // ─── Bağımsız Makbuz + Allocation ───────────────────────────────────────

  const fetchAcikFaturalar = async (cariID: number) => {
    try {
      const res = await axiosInstance.get(`/api/OdemeFaturaEslesmeleri/acik-faturalar/${cariID}`);
      setAcikFaturalar(res.data || []);
    } catch {
      setAcikFaturalar([]);
    }
  };

  const handleMakbuzFormSave = async () => {
    if (!makbuzFormData.miktar || parseFloat(parseMoneyInput(makbuzFormData.miktar)) <= 0) {
      toast.error('Geçerli bir tutar girin.'); return;
    }
    if (!makbuzFormData.tarih) { toast.error('Tarih seçin.'); return; }
    if (!makbuzFormData.kasaID) { toast.error('Kasa seçin.'); return; }
    if (!tahsilatOdemeSelectedCari) return;
    try {
      setMakbuzSaving(true);
      const prefix = tahsilatOdemeModu === 'tahsilat' ? 'TAH' : 'ODE';
      const makbuzTutar = parseFloat(parseMoneyInput(makbuzFormData.miktar));
      const seciliSozlesme = makbuzFormData.sozlesmeID
        ? cariSozlesmeleri.find(s => s.sozlesmeID === parseInt(makbuzFormData.sozlesmeID))
        : null;
      const res = await axiosInstance.post('/api/Odemeler', {
        cariID: tahsilatOdemeSelectedCari,
        islemTarihi: makbuzFormData.tarih,
        miktar: makbuzTutar,
        paraBirimiID: 1,
        islemTakipNo: seciliSozlesme ? seciliSozlesme.sozlesmeTakipNo : `${prefix}-${Date.now()}`,
        odemeTuru: ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 } as Record<string, number>)[makbuzFormData.odemeTuru] ?? 0,
        odemeYonu: tahsilatOdemeModu === 'tahsilat' ? 'Tahsilat' : 'Odeme',
        islemTipi: tahsilatOdemeModu === 'tahsilat' ? 2 : 3,
        odemeDurumu: 'Odendi',
        kasaID: parseInt(makbuzFormData.kasaID),
        aciklama: makbuzFormData.aciklama || null,
        aktifMi: true,
        sozlesmeOdemesi: !!seciliSozlesme,
      });
      const makbuzID = res.data?.odemeID ?? res.data?.id ?? null;
      // Sözleşme ödeme planı taksiti seçildiyse işaretle
      if (secilenPlanTaksit && makbuzID) {
        try {
          await axiosInstance.put(`/api/OdemePlanTaksitleri/${secilenPlanTaksit.taksitID}/tahsilat`, {
            gercekOdemeID: makbuzID,
            tahsilatTarihi: makbuzFormData.tarih,
            kasaID: parseInt(makbuzFormData.kasaID)
          });
        } catch { /* taksit güncellenemedi ama makbuz kaydedildi */ }
      }
      // Otomatik FIFO dağıtım: en eski açık faturadan başla
      try {
        const acikRes = await axiosInstance.get(`/api/OdemeFaturaEslesmeleri/acik-faturalar/${tahsilatOdemeSelectedCari}`);
        const faturalar: AcikFatura[] = acikRes.data || [];
        if (faturalar.length > 0 && makbuzID) {
          let kalan = makbuzTutar;
          const eslesmeler: { faturaOdemeID: number | null; sozlesmeID: number | null; odenenTutar: number }[] = [];
          for (const f of faturalar) {
            if (kalan <= 0) break;
            const dagit = Math.min(kalan, f.kalanTutar);
            eslesmeler.push({
              faturaOdemeID: f.tip === 'odeme' ? f.id : null,
              sozlesmeID: f.tip === 'sozlesme' ? f.id : null,
              odenenTutar: dagit,
            });
            kalan -= dagit;
          }
          if (eslesmeler.length > 0) {
            await axiosInstance.post('/api/OdemeFaturaEslesmeleri', { makbuzOdemeID: makbuzID, eslesmeler });
          }
        }
      } catch (_) { /* Eşleştirme başarısız olsa bile makbuz kaydedildi */ }
      toast.success(`${tahsilatOdemeModu === 'tahsilat' ? 'Tahsilat' : 'Ödeme'} makbuzu kaydedildi!`);
      setShowTahsilatOdemeModal(false);
      fetchOdemeler();
      fetchKasaDashboard();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Makbuz kaydedilemedi.');
    } finally {
      setMakbuzSaving(false);
    }
  };

  const handleEslesmeKaydet = async () => {
    if (!savedMakbuzOdemeID) { toast.error('Makbuz ID bulunamadı.'); return; }
    const eslesmeler = acikFaturalar
      .map(f => {
        const key = `${f.tip}-${f.id}`;
        const tutar = parseFloat(parseMoneyInput(eslesmeTutarlari[key] || '')) || 0;
        if (tutar <= 0) return null;
        return {
          faturaOdemeID: f.tip === 'odeme' ? f.id : null,
          sozlesmeID: f.tip === 'sozlesme' ? f.id : null,
          odenenTutar: tutar,
        };
      })
      .filter(Boolean);
    if (eslesmeler.length === 0) { toast.info('Eşleştirilecek tutar girilmedi.'); return; }
    try {
      setEslesmeSaving(true);
      await axiosInstance.post('/api/OdemeFaturaEslesmeleri', { makbuzOdemeID: savedMakbuzOdemeID, eslesmeler });
      toast.success('Eşleştirmeler kaydedildi!');
      setShowTahsilatOdemeModal(false);
      fetchOdemeler();
      fetchKasaDashboard();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Eşleştirme kaydedilemedi.');
    } finally {
      setEslesmeSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  const handleDelete = async (odeme: Odeme) => {
    if (!window.confirm(`"${odeme.cari.adiSoyadi}" için ${formatCurrency(odeme.miktar)} tutarındaki işlemi silmek istediğinize emin misiniz?`)) return;
    try {
      await axiosInstance.delete(`/api/Odemeler/${odeme.odemeID}`);
      setOdemeler(prev => prev.filter(o => o.odemeID !== odeme.odemeID));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(odeme.odemeID); return next; });
      toast.success('İşlem başarıyla silindi!');
    } catch (error) {
      toast.error('İşlem silinirken bir hata oluştu!');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    const idsToDelete = Array.from(selectedIds);
    if (!window.confirm(`Seçilen ${count} işlemi silmek istediğinize emin misiniz?`)) return;
    try {
      await Promise.all(idsToDelete.map(id => axiosInstance.delete(`/api/Odemeler/${id}`)));
      setOdemeler(prev => prev.filter(o => !idsToDelete.includes(o.odemeID)));
      setSelectedIds(new Set());
      toast.success(`${count} işlem başarıyla silindi!`);
    } catch (error) {
      toast.error('Bazı işlemler silinirken hata oluştu!');
    }
  };

  const handleDeleteGenelIslem = async (g: GenelIslem) => {
    const isVirman = g.islemTipi === 'Virman';
    const confirmMsg = isVirman
      ? 'Bu virman transferini (her iki kasa kaydını da) silmek istediğinize emin misiniz?'
      : 'Bu işlemi silmek istediğinize emin misiniz?';
    if (!window.confirm(confirmMsg)) return;
    try {
      if (isVirman) {
        // Aynı açıklamaya sahip tüm virman kayıtlarını bul ve sil
        const eslesler = genelIslemler.filter(h => h.aciklama === g.aciklama);
        await Promise.all(eslesler.map(h => axiosInstance.delete(`/api/KasaHareketleri/${h.hareketID}`)));
        setGenelIslemler(prev => prev.filter(h => h.aciklama !== g.aciklama));
      } else {
        await axiosInstance.delete(`/api/KasaHareketleri/${g.hareketID}`);
        setGenelIslemler(prev => prev.filter(h => h.hareketID !== g.hareketID));
      }
      fetchKasaDashboard();
      toast.success('İşlem başarıyla silindi!');
    } catch (error) {
      toast.error('İşlem silinirken hata oluştu!');
    }
  };

  const handleExportToExcel = () => {
    try {
      // Excel için veri hazırlama
      const excelData = filteredOdemeler.map((odeme) => {
        // Sözleşme bilgisi varsa formatla
        let sozlesmeInfo = '-';
        if (odeme.sozlesme) {
          const proje = odeme.sozlesme.projeAdi || 'N/A';
          const daire = odeme.sozlesme.daireNo || 'N/A';
          sozlesmeInfo = `${proje} - ${daire}`;
        }

        // Açıklama ve ilgili proje bilgisini birleştir
        let aciklamaText = odeme.aciklama || '-';
        if (odeme.ilgiliProje) {
          aciklamaText += ` (${odeme.ilgiliProje.projeAdi})`;
        }

        return {
          'Müşteri': odeme.cari.adiSoyadi,
          'Miktar': `${odeme.miktar.toLocaleString('tr-TR')} ${odeme.paraBirimi.birim}`,
          'Vade Tarihi': odeme.vadeTarihi ? formatDate(odeme.vadeTarihi) : '-',
          'Ödeme Türü': odeme.odemeTuru,
          'Ödeme Yönü': odeme.odemeYonu,
          'Durum': odeme.odemeDurumu,
          'Sözleşme': sozlesmeInfo,
          'Açıklama': aciklamaText
        };
      });

      // Worksheet oluştur
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Kolon genişliklerini ayarla
      const colWidths = [
        { wch: 25 }, // Müşteri
        { wch: 20 }, // Miktar
        { wch: 15 }, // Vade Tarihi
        { wch: 15 }, // Ödeme Türü
        { wch: 15 }, // Ödeme Yönü
        { wch: 12 }, // Durum
        { wch: 30 }, // Sözleşme
        { wch: 40 }  // Açıklama
      ];
      ws['!cols'] = colWidths;

      // Workbook oluştur
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ödemeler');

      // Dosya adı (tarihli)
      const fileName = `Odemeler_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;

      // Excel dosyasını indir
      XLSX.writeFile(wb, fileName);

      toast.success(`${filteredOdemeler.length} ödeme kaydı Excel'e aktarıldı!`);
    } catch (error) {
      toast.error('Excel dosyası oluşturulurken bir hata oluştu!');
      console.error('Excel export hatası:', error);
    }
  };

  // Sayfalama fonksiyonları
  type UnifiedItem = { type: 'odeme'; item: Odeme } | { type: 'genel'; item: GenelIslem };

  const getUnifiedItems = (): UnifiedItem[] => {
    // genelIslemler (KasaHareketleri, OdemeID=NULL) — nakdi gelir/gider + virman/transfer
    // Bunları her zaman dahil et, ama aktif filtrelere göre filtrele

    let filteredGenel = [...genelIslemler];

    // Tarih filtresi
    if (filters.tarihBaslangic) {
      filteredGenel = filteredGenel.filter(g => !g.tarih || new Date(g.tarih) >= new Date(filters.tarihBaslangic));
    }
    if (filters.tarihBitis) {
      const bitis = new Date(filters.tarihBitis);
      bitis.setHours(23, 59, 59, 999);
      filteredGenel = filteredGenel.filter(g => !g.tarih || new Date(g.tarih) <= bitis);
    }

    // İşlem tipi filtresi — tahsilat/odeme yalnızca Odemeler'de, virman yalnızca genelde
    if (filters.islemTipi === 'tahsilat' || filters.islemTipi === 'odeme') {
      // Bu iki tip genelIslemler'de yok, dahil etme
      filteredGenel = [];
    } else if (filters.islemTipi === 'virman') {
      filteredGenel = filteredGenel.filter(g => g.islemTipi === 'Virman');
    } else {
      // islemTipi === '' (Tümü) → virmanları dahil et ama düz gelir/gider de dahil
    }

    // Tür filtresi (Gelir/Gider) — genelIslemler'de Virman dışındakiler dahil
    if (filters.tur === 'Gelir') {
      filteredGenel = filteredGenel.filter(g => g.islemTipi !== 'Virman' && g.islemTuru === 'Giris');
    } else if (filters.tur === 'Gider') {
      filteredGenel = filteredGenel.filter(g => g.islemTipi !== 'Virman' && g.islemTuru === 'Cikis');
    }

    // Durum filtresi — genelIslemler her zaman "Ödendi" statüsünde
    if (filters.durumlar.length > 0 && !filters.durumlar.includes('Odendi')) {
      filteredGenel = []; // Bekliyor/Gecikmiş seçilmişse nakdi kayıtlar dahil edilmez
    }
    if (filters.durum === 'Bekliyor' || filters.durum === 'Iptal') {
      filteredGenel = []; // Bu durumlar nakdi kayıtlarda yok
    }

    // Müşteri adı filtresi — genelIslemler'de cari bilgisi yok, filtre uygulanmaz
    // (cariID=null oldukları için müşteri adı aramasında dahil edilmez)
    if (filters.musteriAdi.trim()) {
      filteredGenel = [];
    }

    // Proje filtresi
    if (filters.projeSecimi !== 'tumü') {
      if (filters.projeSecimi === 'proje-disi') {
        filteredGenel = filteredGenel.filter(g => !g.proje);
      } else {
        const projeID = parseInt(filters.projeSecimi);
        filteredGenel = filteredGenel.filter(g => g.proje?.projeID === projeID);
      }
    }

    const all: UnifiedItem[] = [
      ...filteredOdemeler.map(o => ({ type: 'odeme' as const, item: o })),
      ...filteredGenel.map(g => ({ type: 'genel' as const, item: g }))
    ];
    return all.sort((a, b) => {
      const [field, dir] = filters.siralama.split('-');
      const mul = dir === 'asc' ? 1 : -1;
      if (field === 'vadeTarihi') {
        const dA = a.type === 'odeme' && (a.item as Odeme).vadeTarihi ? new Date((a.item as Odeme).vadeTarihi!).getTime() : 0;
        const dB = b.type === 'odeme' && (b.item as Odeme).vadeTarihi ? new Date((b.item as Odeme).vadeTarihi!).getTime() : 0;
        return mul * (dA - dB);
      }
      if (field === 'miktar') {
        return mul * (a.item.miktar - b.item.miktar);
      }
      // default: islemTarihi
      const dA = new Date(a.type === 'odeme' ? (a.item as Odeme).islemTarihi : (a.item as GenelIslem).tarih).getTime();
      const dB = new Date(b.type === 'odeme' ? (b.item as Odeme).islemTarihi : (b.item as GenelIslem).tarih).getTime();
      return mul * (dA - dB);
    });
  };

  const getCurrentPageItems = () => {
    const all = getUnifiedItems();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return all.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(getUnifiedItems().length / itemsPerPage);
  };

  const isOverdue = (vadeTarihi?: string, durum?: string) => {
    if (!vadeTarihi || durum === 'Odendi') return false;
    return new Date(vadeTarihi) < new Date();
  };

  const odStat = (label: string, value: number, icon: React.ReactNode, accentColor: string, bgColor: string, count: number, cardBg?: string) => (
    <div style={{ flex: 1, minWidth: 150, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{label}</span>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.4px', lineHeight: 1 }}>
        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(value)}
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
        <span style={{ color: accentColor, fontWeight: 700 }}>{count}</span> kayıt
      </div>
    </div>
  );

  const TogglePill = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
    <button
      onClick={onClick}
      style={{
        padding: '5px 14px', borderRadius: 100,
        border: `1.5px solid ${active ? '#cd6906' : '#e2e8f0'}`,
        background: active ? '#fff7ed' : '#f8fafc',
        color: active ? '#cd6906' : '#64748b',
        fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
        display: 'inline-flex', alignItems: 'center', gap: 6
      }}
    >
      {label}
    </button>
  );

  return (
    <>
      <style>{`
        .od-wrap { background: #f8fafc; min-height: 100vh; padding: 24px; font-family: 'Inter', 'Geist', 'Segoe UI', system-ui, -apple-system, sans-serif; }
        .od-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 16px; flex-wrap: wrap; }
        .od-page-header-left { display: flex; flex-direction: column; gap: 4px; }
        .od-page-title { font-size: 22px; font-weight: 700; color: #0f172a; letter-spacing: -0.4px; margin: 0; }
        .od-page-subtitle { font-size: 13.5px; color: #64748b; margin: 0; }
        .od-filter-card { background: #fff; border-radius: 16px; border: 1.5px solid #f1f5f9; box-shadow: 0 1px 4px rgba(15,23,42,0.05); padding: 12px 16px; margin-bottom: 14px; }
        .od-filter-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .od-adv-filters { overflow: visible; }
        .od-adv-filters.open { max-height: none; opacity: 1; }
        .od-adv-inner { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 10px 0 2px; border-top: 1px solid #f1f5f9; margin-top: 10px; }
        .od-btn-adv { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 8px; border: 1.5px solid #e2e8f0; background: #fff; color: #64748b; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.15s; white-space: nowrap; }
        .od-btn-adv:hover { border-color: #94a3b8; color: #374151; }
        .od-btn-adv.has-active { border-color: #cd6906; color: #cd6906; background: #fff7ed; }
        .od-adv-badge { background: #cd6906; color: #fff; border-radius: 20px; padding: 1px 6px; font-size: 10px; font-weight: 700; margin-left: 1px; }
        .od-field { display: flex; flex-direction: column; gap: 5px; }
        .od-flabel { font-size: 11px; font-weight: 600; color: #94a3b8; letter-spacing: 0.4px; text-transform: uppercase; }
        .od-input { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 8px 12px; font-size: 13px; color: #0f172a; outline: none; font-family: inherit; transition: all 0.15s; }
        .od-input:focus { border-color: #cd6906; box-shadow: 0 0 0 3px rgba(205,105,6,0.08); background: #fff; }
        .od-input:disabled { opacity: 0.4; cursor: not-allowed; }
        .od-input::placeholder { color: #94a3b8; }
        .od-select { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 8px 32px 8px 12px; font-size: 13px; color: #0f172a; outline: none; font-family: inherit; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%2394a3b8' d='M5 7L0 2h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; transition: all 0.15s; cursor: pointer; }
        .od-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.08); background-color: #fff; }
        .od-filter-divider { width: 1px; background: #f1f5f9; height: 36px; flex-shrink: 0; align-self: flex-end; margin: 0 2px; }
        .od-pill-group { display: flex; gap: 6px; flex-wrap: wrap; }
        .od-list-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
        .od-list-title { font-size: 15px; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 8px; }
        .od-count-chip { background: #f1f5f9; color: #64748b; border-radius: 20px; padding: 2px 10px; font-size: 12px; font-weight: 600; }
        .od-btn-dl { background: #fff; border: 1.5px solid #1d6f42; border-radius: 10px; padding: 8px 18px; font-size: 12.5px; color: #1d6f42; cursor: pointer; font-family: inherit; font-weight: 600; transition: all 0.15s; display: flex; align-items: center; gap: 7px; }
        .od-btn-dl:hover { background: #1d6f42; color: #fff; border-color: #1d6f42; } .od-btn-dl:disabled { opacity: 0.4; cursor: not-allowed; }
        .od-btn-temizle { padding: 8px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff; color: #64748b; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.15s; }
        .od-btn-temizle:hover { border-color: #94a3b8; color: #374151; }
        /* Table */
        .od-table-wrap { background: #fff; border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(15,23,42,0.04); overflow-x: auto; }
        .od-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .od-thead th { padding: 11px 16px; font-size: 11px; font-weight: 600; color: #94a3b8; letter-spacing: 0.6px; text-transform: uppercase; background: #f8fafc; border-bottom: 1.5px solid #f1f5f9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .od-row { border-bottom: 1px solid #f1f5f9; transition: background 0.12s; position: relative; }
        .od-row:last-child { border-bottom: none; }
        .od-row:hover { background: #fef9f4; }
        .od-row-overdue { background: #fff5f5; }
        .od-row-overdue:hover { background: #fee2e2; }
        .od-row td { padding: 10px 14px; font-size: 13px; color: #374151; vertical-align: middle; overflow: hidden; }
        .od-cust-name { font-size: 13px; font-weight: 600; color: #111827; cursor: pointer; transition: color 0.12s; }
        .od-cust-name:hover { color: #3b82f6; }
        .od-cust-sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
        .od-miktar { font-size: 13.5px; font-weight: 700; color: #111827; letter-spacing: -0.3px; font-variant-numeric: tabular-nums; }
        .od-birim { font-size: 11px; color: #9ca3af; font-weight: 500; }
        /* Segmented Control */
        .od-segmented { display: inline-flex; background: rgba(241,245,249,0.85); padding: 3px; border-radius: 9px; gap: 2px; }
        .od-seg-btn { padding: 5px 13px; border-radius: 6px; border: none; font-size: 12px; font-weight: 500; cursor: pointer; font-family: inherit; transition: all 0.15s; color: #6b7280; background: transparent; line-height: 1.4; }
        .od-seg-btn:hover { color: #111827; }
        .od-seg-btn.active { background: #ffffff; color: #111827; font-weight: 600; box-shadow: 0 1px 3px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.04); }
        .od-date { display: flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 400; color: #6b7280; }
        .od-date-normal { color: #6b7280; }
        .od-date-overdue { color: #b91c1c; font-weight: 500; }
        .od-date-normal { color: #334155; }
        .od-date-overdue { color: #dc2626; font-weight: 600; }
        .od-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 600; border-radius: 20px; padding: 4px 11px; white-space: nowrap; }
        .od-badge-tahsilat { background: #f0fdf4; color: #16a34a; }
        .od-badge-odeme { background: #fef2f2; color: #dc2626; }
        .od-badge-bekliyor { background: #fef9c3; color: #ca8a04; }
        .od-badge-odendi { background: #f0fdf4; color: #16a34a; }
        .od-badge-tur { background: #eff6ff; color: #1d4ed8; }
        .od-soz-chip { display: inline-flex; align-items: center; gap: 5px; background: #f8fafc; border: 1.5px solid #f1f5f9; border-radius: 9px; padding: 5px 10px; cursor: pointer; transition: all 0.12s; text-decoration: none; white-space: nowrap; }
        .od-soz-chip:hover { border-color: #3b82f6; background: #eff6ff; }
        .od-soz-proje { font-size: 12px; font-weight: 700; color: #0f172a; }
        .od-soz-sep { font-size: 11px; color: #cbd5e1; }
        .od-soz-daire { font-size: 11.5px; color: #64748b; }
        .od-aciklama { font-size: 12px; color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block; }
        .od-edit-btn { width: 30px; height: 30px; border-radius: 8px; background: none; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; cursor: pointer; opacity: 0; pointer-events: none; transition: opacity 0.15s; color: #64748b; font-size: 13px; outline: none; }
        .od-row:hover .od-edit-btn { opacity: 1; pointer-events: auto; }
        .od-edit-btn:hover { background: #fffbeb; border-color: #f59e0b; color: #b45309; }
        .od-edit-btn:focus { opacity: 0; pointer-events: none; }
        /* Pagination */
        .od-pagination { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-top: 1.5px solid #f8fafc; }
        .od-pg-info { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .od-pg-btns { display: flex; gap: 4px; }
        .od-pg-btn { width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid #e2e8f0; background: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 12px; font-weight: 600; color: #64748b; transition: all 0.12s; }
        .od-pg-btn:hover:not(:disabled) { border-color: #3b82f6; color: #3b82f6; }
        .od-pg-btn.active { background: #cd6906; border-color: #cd6906; color: #fff; }
        .od-pg-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .od-pg-size { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .od-pg-size select { border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 4px 8px; font-size: 12px; color: #374151; background: #fff; cursor: pointer; font-family: inherit; outline: none; }
        .od-pg-size select:focus { border-color: #3b82f6; }
        .od-empty { padding: 60px 24px; text-align: center; }
        .od-empty-icon { font-size: 40px; opacity: 0.2; margin-bottom: 12px; }
        .od-empty-text { font-size: 13.5px; color: #94a3b8; font-weight: 500; }
        @keyframes od-spin { to { transform: rotate(360deg); } }
        .od-spinner { width: 30px; height: 30px; border: 3px solid #f1f5f9; border-top-color: #3b82f6; border-radius: 50%; animation: od-spin 0.8s linear infinite; margin: 0 auto 12px; }
        @keyframes od-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.88); } }
        .od-stat-pulse { animation: od-pulse 1.6s ease-in-out infinite; }
        .od-stat-clickable { cursor: pointer; transition: box-shadow 0.15s, transform 0.15s; }
        .od-stat-clickable:hover { box-shadow: 0 4px 16px rgba(220,38,38,0.14) !important; transform: translateY(-1px); }
        /* gecik popup */
        .od-gecik-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.4); z-index: 1060; display: flex; align-items: center; justify-content: center; padding: 24px; backdrop-filter: blur(3px); }
        .od-gecik-modal { background: #fff; border-radius: 18px; width: 100%; max-width: 420px; box-shadow: 0 20px 60px rgba(15,23,42,0.18); overflow: hidden; }
        .od-gecik-modal-head { background: #fef2f2; padding: 20px 24px; display: flex; align-items: center; gap: 12px; border-bottom: 1px solid #fecaca; }
        .od-gecik-modal-body { padding: 22px 24px; }
        .od-gecik-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border-radius: 10px; border: 1px solid #f1f5f9; margin-bottom: 8px; background: #f8fafc; font-size: 13px; }
        .od-gecik-row:last-child { margin-bottom: 0; }
        .od-gecik-name { font-weight: 600; color: #0f172a; }
        .od-gecik-amount { font-weight: 700; color: #dc2626; font-variant-numeric: tabular-nums; }
        .od-gecik-date { font-size: 11.5px; color: #94a3b8; margin-top: 2px; }
        .od-gecik-modal-foot { padding: 14px 24px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; }
        /* Modal */
        .od-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.45); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 24px; backdrop-filter: blur(3px); }
        .od-modal { background: #fff; border-radius: 20px; width: 100%; max-width: 460px; box-shadow: 0 20px 60px rgba(15,23,42,0.18); overflow: hidden; }
        .od-modal-header { padding: 20px 24px; border-bottom: 1.5px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; }
        .od-modal-title { font-size: 15px; font-weight: 700; color: #0f172a; }
        .od-modal-close { width: 30px; height: 30px; border-radius: 8px; background: #f8fafc; border: 1.5px solid #f1f5f9; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 13px; color: #64748b; }
        .od-modal-close:hover { background: #f1f5f9; }
        .od-modal-body { padding: 22px 24px; }
        .od-modal-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 20px; background: #f8fafc; border-radius: 12px; padding: 16px; border: 1.5px solid #f1f5f9; }
        .od-modal-info-item { display: flex; flex-direction: column; gap: 3px; }
        .od-modal-info-label { font-size: 11px; color: #94a3b8; font-weight: 600; letter-spacing: 0.3px; text-transform: uppercase; }
        .od-modal-info-val { font-size: 13px; font-weight: 600; color: #0f172a; }
        .od-modal-footer { padding: 16px 24px; border-top: 1.5px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 10px; }
        .od-modal-btn-cancel { background: none; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 9px 20px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; font-family: inherit; transition: all 0.15s; }
        .od-modal-btn-cancel:hover { border-color: #94a3b8; }
        .od-modal-btn-save { background: #cd6906; border: none; border-radius: 10px; padding: 10px 20px; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; font-family: inherit; transition: background 0.15s, box-shadow 0.15s; box-shadow: 0 4px 12px rgba(205,105,6,0.3); }
        .od-modal-btn-save:hover { background: #b85d05; box-shadow: 0 4px 12px rgba(205,105,6,0.3); }
        .od-modal-select { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; font-size: 13.5px; color: #0f172a; outline: none; font-family: inherit; appearance: none; width: 100%; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%2394a3b8' d='M5 7L0 2h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; }
        .od-modal-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.08); }
        /* Responsive */
        .od-table-wrap table { display: table; width: 100%; }
        .od-mobile-list { display: none; }
        @media (max-width: 768px) {
          .od-wrap { padding: 12px; }
          .od-filter-row { flex-direction: column; align-items: stretch; }
          .od-filter-divider { display: none; }
          .od-list-header { flex-wrap: wrap; gap: 10px; }
          .od-pagination { flex-wrap: wrap; gap: 10px; justify-content: center; }
          .od-page-header { flex-direction: column; gap: 12px; }
          .od-page-header > div:last-child { align-self: flex-start; }
          .od-table-wrap table { display: none; }
          .od-mobile-list { display: flex; flex-direction: column; }
        }
        /* Card list */
        .od-card { padding: 14px 16px; border-bottom: 1px solid #f1f5f9; display: flex; flex-direction: row; align-items: center; gap: 12px; background: #fff; transition: background 0.15s, box-shadow 0.15s, border-color 0.15s; cursor: default; }
        .od-card:last-child { border-bottom: none; }
        .od-card:hover { background: #fafbff; box-shadow: 0 2px 12px rgba(100,116,139,0.10); border-bottom-color: #e2e8f0; z-index: 1; position: relative; }
        .od-card:active { background: #fef9f4; }
        .od-card-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
        .od-card-name { font-size: 13.5px; font-weight: 700; color: #111827; cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .od-card-firma { font-size: 12px; color: #6b7280; font-weight: 400; }
        .od-card-badges { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .od-card-right { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; flex-shrink: 0; }
        .od-card-amount { font-size: 15px; font-weight: 800; color: #111827; letter-spacing: -0.4px; font-variant-numeric: tabular-nums; white-space: nowrap; text-align: right; }
        .od-card-aciklama { font-size: 12px; color: #94a3b8; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 180px; text-align: right; }
        .od-gecik-badge { font-size: 10px; background: #fee2e2; color: #b91c1c; border-radius: 4px; padding: 1px 5px; }
        .od-card-edit-btn { width: 34px; height: 34px; border-radius: 8px; background: none; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #64748b; font-size: 13px; outline: none; flex-shrink: 0; }
        .od-card-edit-btn:active { background: #fffbeb; border-color: #f59e0b; color: #b45309; }
        .od-card-genel { border-left: 3px solid #e2e8f0; }
        .od-card-genel:hover { border-left-color: #94a3b8; }
        /* Action icon buttons */
        .od-action-btn { width: 28px; height: 28px; border-radius: 7px; background: none; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s; outline: none; padding: 0; flex-shrink: 0; }
        .od-action-view { color: #94a3b8; } .od-action-view:hover { color: #3b82f6; background: #eff6ff; }
        .od-action-edit { color: #94a3b8; } .od-action-edit:hover { color: #f59e0b; background: #fffbeb; }
        .od-action-delete { color: #94a3b8; } .od-action-delete:hover { color: #ef4444; background: #fef2f2; }
        /* Pill badges */
        .od-pill { display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; font-weight: 600; border-radius: 100px; padding: 3px 10px; white-space: nowrap; }
        .od-pill-green { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
        .od-pill-yellow { background: #fefce8; color: #a16207; border: 1px solid #fef08a; }
        .od-pill-red { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .od-pill-blue { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
        .od-pill-slate { background: #f8fafc; color: #475569; border: 1px solid #e2e8f0; }
      `}</style>

      <div className="od-wrap">

        {/* Page Header */}
        <div className="od-page-header">
          <div className="od-page-header-left">
            <h1 className="od-page-title">İşlemler</h1>
            <p className="od-page-subtitle">Tüm tahsilat ve ödeme işlemlerini buradan takip edin.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <button
            onClick={handleYeniIslem}
            style={{
              background: '#cd6906',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '10px 22px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 12px rgba(205,105,6,0.3)',
              transition: 'background 0.15s',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            <i className="fas fa-plus"></i> Yeni İşlem
          </button>
          <button className="od-btn-dl" onClick={handleExportToExcel} disabled={loading}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="15" height="15"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
            Excel İndir
          </button>
          </div>
        </div>

        {/* Stats Row */}
        {loading ? (
          <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #f1f5f9', padding: '50px 24px', textAlign: 'center', marginBottom: 22 }}>
            <div className="od-spinner" />
            <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>Ödemeler yükleniyor...</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              {odStat('Toplam Tutar', calculateToplamOdeme(),
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#cd6906" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" /></svg>,
                '#cd6906', '#fff7ed', filteredOdemeler.length)}
              {odStat('Bekleyen Ödemeler', calculateBekleyenOdemeler(),
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#ca8a04" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>,
                '#ca8a04', '#fef9c3',
                filteredOdemeler.filter(o => o.odemeDurumu === 'Bekliyor' && o.odemeYonu === 'Odeme').length)}
              {odStat('Bekleyen Tahsilatlar', calculateBekleyenTahsilatlar(),
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#2563eb" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" /></svg>,
                '#2563eb', '#dbeafe',
                filteredOdemeler.filter(o => o.odemeDurumu === 'Bekliyor' && o.odemeYonu === 'Tahsilat').length)}
              {odStat('Geciken Ödemeler', calculateGecenOdemeler(),
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#dc2626" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>,
                '#dc2626', '#fef2f2',
                filteredOdemeler.filter(o => isOverdue(o.vadeTarihi, o.odemeDurumu) && o.odemeYonu === 'Odeme').length, '#fef2f2')}
              {/* Geciken Tahsilatlar — clickable pulse card */}
              {(() => {
                const gecikCount = filteredOdemeler.filter(o => isOverdue(o.vadeTarihi, o.odemeDurumu) && o.odemeYonu === 'Tahsilat').length;
                const gecikValue = calculateGecenTahsilatlar();
                const hasGecik = gecikCount > 0;
                return (
                  <div
                    className={`od-stat-clickable`}
                    onClick={() => hasGecik && setShowGecikPopup(true)}
                    style={{ flex: 1, minWidth: 150, background: hasGecik ? '#fef2f2' : '#fff', borderRadius: 12, border: hasGecik ? '1.5px solid #fecaca' : '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', padding: '12px 16px', cursor: hasGecik ? 'pointer' : 'default' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: hasGecik ? '#b91c1c' : '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase' }}>Geciken Tahsilatlar</span>
                      <div className={hasGecik ? 'od-stat-pulse' : ''} style={{ width: 28, height: 28, borderRadius: 8, background: hasGecik ? '#fee2e2' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke={hasGecik ? '#dc2626' : '#475569'} width="15" height="15"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </div>
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: hasGecik ? '#b91c1c' : '#0f172a', letterSpacing: '-0.4px', lineHeight: 1 }}>
                      {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(gecikValue)}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                      <span style={{ color: hasGecik ? '#dc2626' : '#475569', fontWeight: 700 }}>{gecikCount}</span> kayıt
                      {hasGecik && <span style={{ marginLeft: 8, fontSize: 10, color: '#dc2626', opacity: 0.7 }}>· görüntüle →</span>}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Filter Card */}
            {(() => {
              const y = new Date().getFullYear();
              const defaultBaslangic = `${y}-01-01`;
              const defaultBitis = `${y}-12-31`;
              const isDateChanged = filters.tarihBaslangic !== defaultBaslangic || filters.tarihBitis !== defaultBitis;
              const isFilterActive = isDateChanged || filters.projeSecimi !== 'tumü' || filters.musteriAdi !== '' || filters.islemTipi !== '' || filters.tur !== '' || filters.siralama !== 'islemTarihi-desc' || filters.durum !== '';

              const setQuickDate = (start: string, end: string) => {
                setFilters(prev => ({ ...prev, tarihBaslangic: start, tarihBitis: end }));
                setDateDisplays({ baslangic: isoToDisplay(start), bitis: isoToDisplay(end) });
              };
              const today = new Date().toISOString().split('T')[0];
              const getWeek = () => {
                const now = new Date();
                const day = now.getDay() === 0 ? 7 : now.getDay();
                const s = new Date(now); s.setDate(now.getDate() - day + 1); s.setHours(0,0,0,0);
                const e = new Date(s); e.setDate(s.getDate() + 6);
                return { s: s.toISOString().split('T')[0], e: e.toISOString().split('T')[0] };
              };
              const getMonth = () => {
                const now = new Date();
                const s = new Date(now.getFullYear(), now.getMonth(), 1);
                const e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                return { s: s.toISOString().split('T')[0], e: e.toISOString().split('T')[0] };
              };
              const w = getWeek(); const m = getMonth();
              const isToday = filters.tarihBaslangic === today && filters.tarihBitis === today;
              const isThisWeek = filters.tarihBaslangic === w.s && filters.tarihBitis === w.e;
              const isThisMonth = filters.tarihBaslangic === m.s && filters.tarihBitis === m.e;
              const isBuguneKadar = filters.tarihBaslangic === `${y}-01-01` && filters.tarihBitis === today && !isToday;

              return (
                <div className="od-filter-card">
                  {/* Satır 1: Arama + İşlem Türü + Durum + Temizle */}
                  <div className="od-filter-row" style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
                    <input type="text" className="od-input" placeholder="Müşteri adı ara..." style={{ flex: 1, minWidth: 140 }}
                      value={filters.musteriAdi}
                      onChange={e => handleFilterChange('musteriAdi', e.target.value)} />
                    <div style={{ width: 1, height: 28, background: '#f1f5f9', flexShrink: 0 }} />
                    <div className="od-segmented">
                      <button className={`od-seg-btn${filters.islemTipi === '' ? ' active' : ''}`} onClick={() => handleFilterChange('islemTipi', '')}>Tümü</button>
                      <button className={`od-seg-btn${filters.islemTipi === 'tahsilat' ? ' active' : ''}`} onClick={() => handleFilterChange('islemTipi', 'tahsilat')}>Tahsilat</button>
                      <button className={`od-seg-btn${filters.islemTipi === 'odeme' ? ' active' : ''}`} onClick={() => handleFilterChange('islemTipi', 'odeme')}>Ödeme</button>
                      <button className={`od-seg-btn${filters.islemTipi === 'virman' ? ' active' : ''}`} onClick={() => handleFilterChange('islemTipi', 'virman')}>Virman</button>
                    </div>
                    <div style={{ width: 1, height: 28, background: '#f1f5f9', flexShrink: 0 }} />
                    <div className="od-segmented">
                      <button className={`od-seg-btn${filters.tur === '' ? ' active' : ''}`} onClick={() => handleFilterChange('tur', '')}>Tümü</button>
                      <button className={`od-seg-btn${filters.tur === 'Gelir' ? ' active' : ''}`} onClick={() => handleFilterChange('tur', 'Gelir')}>Gelir</button>
                      <button className={`od-seg-btn${filters.tur === 'Gider' ? ' active' : ''}`} onClick={() => handleFilterChange('tur', 'Gider')}>Gider</button>
                    </div>
                    <div style={{ width: 1, height: 28, background: '#f1f5f9', flexShrink: 0 }} />
                    <div className="od-segmented">
                      <button className={`od-seg-btn${filters.durum === '' ? ' active' : ''}`} onClick={() => handleFilterChange('durum', '')}>Tümü</button>
                      <button className={`od-seg-btn${filters.durum === 'Bekliyor' ? ' active' : ''}`} onClick={() => handleFilterChange('durum', 'Bekliyor')}>Bekliyor</button>
                      <button className={`od-seg-btn${filters.durum === 'Odendi' ? ' active' : ''}`} onClick={() => handleFilterChange('durum', 'Odendi')}>Ödendi</button>
                    </div>
                    {isFilterActive && (
                      <button className="od-btn-temizle" onClick={clearFilters}>Temizle</button>
                    )}
                  </div>
                  {/* Satır 2: Tarih Aralığı + Hızlı + Proje */}
                  <div className="od-filter-row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {/* Başlangıç tarihi — metin gösterimi + gizli takvim */}
                      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                        <input type="text" className="od-input" style={{ width: 110, fontSize: 12.5, paddingRight: 26 }}
                          value={dateDisplays.baslangic}
                          placeholder="GG.AA.YYYY"
                          onChange={e => handleDateDisplayChange('baslangic', e.target.value)} />
                        <input type="date" value={filters.tarihBaslangic}
                          onChange={e => {
                            const val = e.target.value;
                            handleFilterChange('tarihBaslangic', val);
                            setDateDisplays(prev => ({ ...prev, baslangic: isoToDisplay(val) }));
                          }}
                          style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, opacity: 0, cursor: 'pointer', border: 'none', padding: 0 }} />
                        <i className="fas fa-calendar-alt" style={{ position: 'absolute', right: 7, fontSize: 11, color: '#94a3b8', pointerEvents: 'none' }} />
                      </div>
                      <span style={{ color: '#94a3b8', fontSize: 13, flexShrink: 0 }}>&#8212;</span>
                      {/* Bitiş tarihi — metin gösterimi + gizli takvim */}
                      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                        <input type="text" className="od-input" style={{ width: 110, fontSize: 12.5, paddingRight: 26 }}
                          value={dateDisplays.bitis}
                          placeholder="GG.AA.YYYY"
                          onChange={e => handleDateDisplayChange('bitis', e.target.value)} />
                        <input type="date" value={filters.tarihBitis}
                          onChange={e => {
                            const val = e.target.value;
                            handleFilterChange('tarihBitis', val);
                            setDateDisplays(prev => ({ ...prev, bitis: isoToDisplay(val) }));
                          }}
                          style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, opacity: 0, cursor: 'pointer', border: 'none', padding: 0 }} />
                        <i className="fas fa-calendar-alt" style={{ position: 'absolute', right: 7, fontSize: 11, color: '#94a3b8', pointerEvents: 'none' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                      <button
                        onClick={() => setQuickDate(today, today)}
                        style={{ padding: '5px 11px', borderRadius: 100, border: `1.5px solid ${isToday ? '#cd6906' : '#e2e8f0'}`, background: isToday ? '#fff7ed' : '#f8fafc', color: isToday ? '#cd6906' : '#64748b', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                      >Bugün</button>
                      <button
                        onClick={() => setQuickDate(`${y}-01-01`, today)}
                        style={{ padding: '5px 11px', borderRadius: 100, border: `1.5px solid ${isBuguneKadar ? '#cd6906' : '#e2e8f0'}`, background: isBuguneKadar ? '#fff7ed' : '#f8fafc', color: isBuguneKadar ? '#cd6906' : '#64748b', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                      >Bugüne Kadar</button>
                      <button
                        onClick={() => setQuickDate(w.s, w.e)}
                        style={{ padding: '5px 11px', borderRadius: 100, border: `1.5px solid ${isThisWeek ? '#cd6906' : '#e2e8f0'}`, background: isThisWeek ? '#fff7ed' : '#f8fafc', color: isThisWeek ? '#cd6906' : '#64748b', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                      >Bu Hafta</button>
                      <button
                        onClick={() => setQuickDate(m.s, m.e)}
                        style={{ padding: '5px 11px', borderRadius: 100, border: `1.5px solid ${isThisMonth ? '#cd6906' : '#e2e8f0'}`, background: isThisMonth ? '#fff7ed' : '#f8fafc', color: isThisMonth ? '#cd6906' : '#64748b', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                      >Bu Ay</button>
                      <button
                        onClick={() => setQuickDate(`${y}-01-01`, `${y}-12-31`)}
                        style={{ padding: '5px 11px', borderRadius: 100, border: `1.5px solid ${!isDateChanged ? '#cd6906' : '#e2e8f0'}`, background: !isDateChanged ? '#fff7ed' : '#f8fafc', color: !isDateChanged ? '#cd6906' : '#64748b', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                      >Bu Yıl</button>
                    </div>
                    <div style={{ width: 1, height: 28, background: '#f1f5f9', flexShrink: 0 }} />
                    <div className="od-field">
                      <select className="od-select" style={{ fontSize: 12.5 }} value={filters.projeSecimi} onChange={e => handleFilterChange('projeSecimi', e.target.value)}>
                        <option value="tumü">Tüm Projeler</option>
                        <option value="proje-disi">Proje Dışı</option>
                        {projeler.map(p => (<option key={p.projeID} value={p.projeID.toString()}>{p.projeAdi}</option>))}
                      </select>
                    </div>
                    <div style={{ width: 1, height: 28, background: '#f1f5f9', flexShrink: 0 }} />
                    <div className="od-field">
                      <select className="od-select" style={{ fontSize: 12.5 }} value={filters.siralama} onChange={e => handleFilterChange('siralama', e.target.value)}>
                        <option value="islemTarihi-desc">İşlem Tarihi ↓</option>
                        <option value="islemTarihi-asc">İşlem Tarihi ↑</option>
                        <option value="vadeTarihi-desc">Vade Tarihi ↓</option>
                        <option value="vadeTarihi-asc">Vade Tarihi ↑</option>
                        <option value="miktar-desc">Miktar ↓</option>
                        <option value="miktar-asc">Miktar ↑</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* List Header */}
            <div className="od-list-header">
              <div className="od-list-title">
                İşlemler
                <span className="od-count-chip">{filteredOdemeler.length}</span>
              </div>
              {selectedIds.size > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '6px 14px', fontSize: 13, color: '#c2410c' }}>
                  <span style={{ fontWeight: 600 }}>{selectedIds.size} işlem seçildi</span>
                  <button onClick={handleBulkDelete} style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#ef4444', color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Seçilenleri Sil</button>
                  <button onClick={() => setSelectedIds(new Set())} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fed7aa', background: 'transparent', color: '#c2410c', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>İptal</button>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="od-table-wrap">
              {filteredOdemeler.length === 0 ? (
                <div className="od-empty">
                  <div className="od-empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#cbd5e1" width="40" height="40"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>
                  </div>
                  <div className="od-empty-text">Filtrelere uygun ödeme kaydı bulunamadı.</div>
                </div>
              ) : (
                <>
                  <table className="od-table">
                    <thead className="od-thead">
                      {(() => {
                        const pOdemeIds = getCurrentPageItems().filter(u => u.type === 'odeme').map(u => (u.item as Odeme).odemeID);
                        const allSel = pOdemeIds.length > 0 && pOdemeIds.every(id => selectedIds.has(id));
                        return (
                          <tr>
                            <th style={{ width: 36, textAlign: 'center' }}>
                              <input type="checkbox" checked={allSel} onChange={e => {
                                if (e.target.checked) setSelectedIds(prev => { const next = new Set(prev); pOdemeIds.forEach(id => next.add(id)); return next; });
                                else setSelectedIds(prev => { const next = new Set(prev); pOdemeIds.forEach(id => next.delete(id)); return next; });
                              }} />
                            </th>
                            <th style={{ width: 88 }}>İşlem Tarihi</th>
                            <th style={{ width: 88 }}>Vade Tarihi</th>
                            <th style={{ width: '16%' }}>Cari</th>
                            <th style={{ width: '24%' }}>Açıklama</th>
                            <th style={{ width: 90 }}>Tür</th>
                            <th style={{ width: 130, textAlign: 'right' }}>Tutar</th>
                            <th style={{ width: 80 }}>Durum</th>
                            <th style={{ width: 88, textAlign: 'center' }}>İşlemler</th>
                          </tr>
                        );
                      })()}
                    </thead>
                    <tbody>
                      {getCurrentPageItems().map(unified => {
                        if (unified.type === 'genel') {
                          const g = unified.item;
                          const isGiris = g.islemTuru === 'Giris';
                          return (
                            <tr key={`g-${g.hareketID}`} className="od-row">
                              <td style={{ width: 36 }}></td>
                              <td>
                                <div className="od-date od-date-normal">{formatDate(g.tarih)}</div>
                              </td>
                              <td><span style={{ color: '#cbd5e1' }}>—</span></td>
                              <td style={{ maxWidth: 180, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>
                                <span className="od-cust-name" style={{ color: isGiris ? '#15803d' : '#b91c1c' }}>
                                  {g.islemTipi === 'Virman' ? 'Kasa Transferi' : (g.kategori?.kategoriAdi ?? 'Genel İşlem')}
                                </span>
                              </td>
                              <td style={{ maxWidth: 240, overflow: 'hidden', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                                {g.kasa && <span style={{ fontSize: 12, color: '#6b7280' }}>{g.kasa.kasaAdi}</span>}
                                {g.kasa && g.aciklama && <span style={{ color: '#cbd5e1', margin: '0 4px' }}>·</span>}
                                {g.aciklama && <span className="od-aciklama" title={g.aciklama}>{g.aciklama}</span>}
                                {!g.kasa && !g.aciklama && <span style={{ color: '#cbd5e1' }}>—</span>}
                              </td>
                              <td>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <span className="od-pill od-pill-slate">{g.kasa?.kasaTuru ?? '—'}</span>
                                  {g.islemTipi === 'Virman' ? (
                                    <span className="od-pill od-pill-slate">⇄ Virman</span>
                                  ) : (
                                    <span className={`od-pill ${isGiris ? 'od-pill-green' : 'od-pill-red'}`}>
                                      {isGiris ? '↓ Gelir' : '↑ Gider'}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <span className="od-miktar" style={{ color: isGiris ? '#15803d' : '#b91c1c' }}>
                                  {isGiris ? '+' : '-'}{new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(g.miktar)}
                                </span>
                              </td>
                              <td><span className="od-pill od-pill-green">Ödendi</span></td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                  {g.proje && (
                                    <button className="od-action-btn od-action-view" title={g.proje.projeAdi}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    </button>
                                  )}
                                  <button className="od-action-btn od-action-delete" title="Sil" onClick={() => handleDeleteGenelIslem(g)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        }
                        const odeme = unified.item;
                        const overdue = isOverdue(odeme.vadeTarihi, odeme.odemeDurumu);
                        return (
                          <tr key={`o-${odeme.odemeID}`} className={`od-row${overdue ? ' od-row-overdue' : ''}`}>
                            <td style={{ width: 36, textAlign: 'center' }}>
                              <input type="checkbox" checked={selectedIds.has(odeme.odemeID)} onChange={e => {
                                setSelectedIds(prev => { const next = new Set(prev); if (e.target.checked) next.add(odeme.odemeID); else next.delete(odeme.odemeID); return next; });
                              }} />
                            </td>
                            <td>
                              <div className="od-date od-date-normal">{formatDate(odeme.islemTarihi)}</div>
                            </td>
                            <td>
                              {odeme.vadeTarihi ? (
                                <div className={`od-date ${overdue ? 'od-date-overdue' : 'od-date-normal'}`}>
                                  {formatDate(odeme.vadeTarihi)}
                                </div>
                              ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            <td style={{ maxWidth: 180, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>
                              <span className="od-cust-name" onClick={() => handleMusteriDetayGit(odeme.cari.cariID)} title={odeme.cari.firmaAdi ? `${odeme.cari.adiSoyadi} · ${odeme.cari.firmaAdi}` : odeme.cari.adiSoyadi}>
                                {odeme.cari.adiSoyadi}
                              </span>
                              {odeme.cari.firmaAdi && (
                                <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 5 }}>· {odeme.cari.firmaAdi}</span>
                              )}
                            </td>
                            <td style={{ maxWidth: 240, overflow: 'hidden', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                              {odeme.sozlesme ? (
                                <div className="od-soz-chip" onClick={() => handleSozlesmeDetayGit(odeme.islemTakipNo)}>
                                  <span className="od-soz-proje">{odeme.sozlesme.projeAdi}</span>
                                  {odeme.sozlesme.daireNo && <><span className="od-soz-sep">·</span><span className="od-soz-daire">Daire {odeme.sozlesme.daireNo}</span></>}
                                </div>
                              ) : odeme.aciklama ? (
                                <span className="od-aciklama" title={`${odeme.aciklama}${odeme.ilgiliProje ? ' · ' + odeme.ilgiliProje.projeAdi : ''}`}>
                                  {odeme.aciklama}{odeme.ilgiliProje && ` · ${odeme.ilgiliProje.projeAdi}`}
                                </span>
                              ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            <td>
                              {(() => {
                                const tur = odeme.islemTipi ?? '';
                                const pillMap: Record<string, string> = { 'Gelir': 'od-pill-green', 'Tahsilat': 'od-pill-blue', 'Gider': 'od-pill-red', 'Odeme': 'od-pill-yellow', 'Virman': 'od-pill-slate' };
                                return <span className={`od-pill ${pillMap[tur] ?? 'od-pill-slate'}`}>{tur || '—'}</span>;
                              })()}
                            </td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <span className="od-miktar">
                                {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(odeme.miktar)}
                              </span>
                              {' '}
                              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>{odeme.paraBirimi.birim}</span>
                            </td>
                            <td>
                              {(odeme.islemTakipNo.startsWith('GEL-') || odeme.islemTakipNo.startsWith('GID-')) ? (
                                <span style={{ color: '#cbd5e1' }}>—</span>
                              ) : (
                                <span className={`od-pill ${odeme.odemeDurumu === 'Odendi' ? 'od-pill-green' : 'od-pill-yellow'}`}>
                                  {odeme.odemeDurumu === 'Odendi' ? 'Ödendi' : 'Bekliyor'}
                                </span>
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                <button className="od-action-btn od-action-view" title="Görüntüle" onClick={() => handleMusteriDetayGit(odeme.cari.cariID)}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                </button>
                                {(!odeme.islemTakipNo.startsWith('GEL-') && !odeme.islemTakipNo.startsWith('GID-')) && (
                                  <button className="od-action-btn od-action-edit" title="Düzenle" onClick={() => handleDuzenleClick(odeme)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                                  </button>
                                )}
                                <button className="od-action-btn od-action-delete" title="Sil" onClick={() => handleDelete(odeme)}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Mobile card list */}
                  <div className="od-mobile-list">
                    {getCurrentPageItems().map(unified => {
                      if (unified.type === 'genel') {
                        const g = unified.item;
                        const isGiris = g.islemTuru === 'Giris';
                        return (
                          <div key={`g-${g.hareketID}`} className="od-card od-card-genel">
                            <div className="od-card-info">
                              <div className="od-card-name" style={{ color: isGiris ? '#15803d' : '#b91c1c' }}>
                                {g.kategori?.kategoriAdi ?? 'Genel İşlem'}
                              </div>
                              <div className="od-card-badges">
                                <span className="od-badge od-badge-tur">{g.kasa?.kasaAdi ?? 'Kasa'}</span>
                                <span className={`od-badge ${isGiris ? 'od-badge-tahsilat' : 'od-badge-odeme'}`}>
                                  {isGiris ? '↓ Gelir' : '↑ Gider'}
                                </span>
                                <span className="od-badge od-badge-odendi">Ödendi</span>
                                <div className="od-date od-date-normal">
                                  <i className="fas fa-calendar-alt" style={{ fontSize: 11 }}></i>
                                  {formatDate(g.tarih)}
                                </div>
                                {g.proje && <span className="od-soz-chip"><span className="od-soz-proje">{g.proje.projeAdi}</span></span>}
                              </div>
                            </div>
                            <div className="od-card-right">
                              <div className="od-card-amount" style={{ color: isGiris ? '#15803d' : '#b91c1c' }}>
                                {isGiris ? '+' : '-'}{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(g.miktar)}
                              </div>
                              {g.aciklama && <div className="od-card-aciklama" title={g.aciklama}>{g.aciklama}</div>}
                            </div>
                          </div>
                        );
                      }
                      const odeme = unified.item;
                      const overdue = isOverdue(odeme.vadeTarihi, odeme.odemeDurumu);
                      return (
                        <div key={`o-${odeme.odemeID}`} className="od-card">
                          {/* Sol: isim + badge'lar */}
                          <div className="od-card-info">
                            <div className="od-card-name" onClick={() => handleMusteriDetayGit(odeme.cari.cariID)}>
                              {odeme.cari.adiSoyadi}
                              {odeme.cari.firmaAdi && <span className="od-card-firma"> · {odeme.cari.firmaAdi}</span>}
                            </div>
                            <div className="od-card-badges">
                              <span className="od-badge od-badge-tur">{odeme.odemeTuru}</span>
                              <span className={`od-badge ${odeme.odemeYonu === 'Tahsilat' ? 'od-badge-tahsilat' : 'od-badge-odeme'}`}>
                                {odeme.odemeYonu === 'Tahsilat' ? '↓ Tahsilat' : '↑ Ödeme'}
                              </span>
                              {(!odeme.islemTakipNo.startsWith('GEL-') && !odeme.islemTakipNo.startsWith('GID-')) && (
                                <span className={`od-badge ${odeme.odemeDurumu === 'Odendi' ? 'od-badge-odendi' : 'od-badge-bekliyor'}`}>
                                  {odeme.odemeDurumu === 'Odendi' ? 'Ödendi' : 'Bekliyor'}
                                </span>
                              )}
                              {odeme.vadeTarihi && (
                                <div className={`od-date ${overdue ? 'od-date-overdue' : 'od-date-normal'}`}>
                                  <i className="fas fa-calendar-alt" style={{ fontSize: 11 }}></i>
                                  {formatDate(odeme.vadeTarihi)}
                                  {overdue && <span className="od-gecik-badge">Gecikmeli</span>}
                                </div>
                              )}
                              {odeme.sozlesme && (
                                <div className="od-soz-chip" onClick={() => handleSozlesmeDetayGit(odeme.islemTakipNo)}>
                                  <span className="od-soz-proje">{odeme.sozlesme.projeAdi}</span>
                                  {odeme.sozlesme.daireNo && <><span className="od-soz-sep">·</span><span className="od-soz-daire">Daire {odeme.sozlesme.daireNo}</span></>}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Sağ: tutar + açıklama */}
                          <div className="od-card-right">
                            <div className="od-card-amount">
                              {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(odeme.miktar)}
                              {odeme.paraBirimi.birim !== 'TRY' && <span className="od-birim" style={{ marginLeft: 4 }}>{odeme.paraBirimi.birim}</span>}
                            </div>
                            {odeme.aciklama && <div className="od-card-aciklama" title={odeme.aciklama}>{odeme.aciklama}</div>}
                          </div>
                          {/* Aksiyonlar */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {(!odeme.islemTakipNo.startsWith('GEL-') && !odeme.islemTakipNo.startsWith('GID-')) && (
                              <button className="od-action-btn od-action-edit" style={{ width: 32, height: 32 }} onClick={() => handleDuzenleClick(odeme)} title="Düzenle">
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                              </button>
                            )}
                            <button className="od-action-btn od-action-delete" style={{ width: 32, height: 32 }} onClick={() => handleDelete(odeme)} title="Sil">
                              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {getUnifiedItems().length > 0 && (
                    <div className="od-pagination">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div className="od-pg-info">
                          {getUnifiedItems().length > itemsPerPage
                            ? `${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, getUnifiedItems().length)} / ${getUnifiedItems().length} kayıt`
                            : `${getUnifiedItems().length} kayıt`}
                        </div>
                        <div className="od-pg-size">
                          <span>Sayfa başına:</span>
                          <select value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>
                      {getTotalPages() > 1 && (
                        <div className="od-pg-btns">
                          <button className="od-pg-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
                          <button className="od-pg-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
                          {Array.from({ length: getTotalPages() }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === getTotalPages() || Math.abs(p - currentPage) <= 1)
                            .map((p, idx, arr) => (
                              <React.Fragment key={p}>
                                {idx > 0 && arr[idx - 1] < p - 1 && (
                                  <span style={{ padding: '0 4px', color: '#94a3b8', lineHeight: '32px' }}>…</span>
                                )}
                                <button className={`od-pg-btn${p === currentPage ? ' active' : ''}`}
                                  onClick={() => setCurrentPage(p)}>{p}</button>
                              </React.Fragment>
                            ))}
                          <button className="od-pg-btn" onClick={() => setCurrentPage(p => Math.min(getTotalPages(), p + 1))} disabled={currentPage === getTotalPages()}>›</button>
                          <button className="od-pg-btn" onClick={() => setCurrentPage(getTotalPages())} disabled={currentPage === getTotalPages()}>»</button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Geciken Tahsilatlar Popup */}
      {showGecikPopup && (
        <div className="od-gecik-overlay" onClick={() => setShowGecikPopup(false)}>
          <div className="od-gecik-modal" onClick={(e) => e.stopPropagation()}>
            <div className="od-gecik-modal-head">
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#dc2626" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#b91c1c' }}>Geciken Tahsilatlar</div>
                <div style={{ fontSize: 12.5, color: '#dc2626', marginTop: 2 }}>
                  Toplam {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(calculateGecenTahsilatlar())} gecikmiş tahsilat
                </div>
              </div>
              <button onClick={() => setShowGecikPopup(false)} style={{ width: 28, height: 28, borderRadius: 7, background: '#fee2e2', border: '1px solid #fecaca', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b91c1c', fontSize: 12, flexShrink: 0 }}>✕</button>
            </div>
            <div className="od-gecik-modal-body" style={{ maxHeight: 360, overflowY: 'auto' }}>
              {filteredOdemeler
                .filter(o => isOverdue(o.vadeTarihi, o.odemeDurumu) && o.odemeYonu === 'Tahsilat')
                .map(o => (
                  <div key={o.odemeID} className="od-gecik-row">
                    <div>
                      <div className="od-gecik-name" style={{ cursor: 'pointer' }} onClick={() => { setShowGecikPopup(false); handleMusteriDetayGit(o.cari.cariID); }}>{o.cari.adiSoyadi}</div>
                      {o.cari.firmaAdi && <div className="od-gecik-date">{o.cari.firmaAdi}</div>}
                      {o.vadeTarihi && <div className="od-gecik-date">Vade: {formatDate(o.vadeTarihi)}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="od-gecik-amount">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(o.miktar)}</div>
                      <div className="od-gecik-date">{o.paraBirimi.birim}</div>
                    </div>
                  </div>
                ))}
            </div>
            <div className="od-gecik-modal-foot">
              <button onClick={() => setShowGecikPopup(false)} style={{ background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 9, padding: '8px 18px', fontSize: 13, fontWeight: 600, color: '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* İşlem Seçim Modal — Wizard Adım 1 */}
      {showIslemSecimModal && (() => {
        const turOptions: Record<'gelir' | 'gider' | 'transfer', Array<{ key: 'gelir' | 'gider' | 'tahsilat' | 'odeme'; label: string; desc: string }>> = {
          gelir: [
            { key: 'gelir', label: 'Gelir İşlemi', desc: 'Gelir kaydı oluştur' },
            { key: 'tahsilat', label: 'Tahsilat', desc: 'Alacak tahsilatı' },
          ],
          gider: [
            { key: 'gider', label: 'Gider İşlemi', desc: 'Gider kaydı oluştur' },
            { key: 'odeme', label: 'Ödeme', desc: 'Borç ödemesi' },
          ],
          transfer: [
            { key: 'gelir', label: 'Transfer Girişi', desc: 'Kasalar arası giriş' },
            { key: 'gider', label: 'Transfer Çıkışı', desc: 'Kasalar arası çıkış' },
          ],
        };
        return (
          <div
            onClick={() => setShowIslemSecimModal(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 1050,
              background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 16, width: '100%', maxWidth: 512,
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '24px 28px 16px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: '#111827', letterSpacing: '-0.01em' }}>Yeni İşlem Ekle</div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>Eklemek istediğiniz finansal işlemin yönünü ve türünü seçin.</div>
                </div>
                <button
                  onClick={() => setShowIslemSecimModal(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, borderRadius: 8, display: 'flex', alignItems: 'center' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#4b5563')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* Step 1 — İşlem Yönü */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Adım 1 — İşlem Yönü</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    {([
                      { key: 'gelir' as const, label: 'Gelir', Icon: ArrowDownRight, iconColor: '#16a34a', iconBg: '#dcfce7' },
                      { key: 'gider' as const, label: 'Gider', Icon: ArrowUpRight, iconColor: '#dc2626', iconBg: '#fee2e2' },
                      { key: 'transfer' as const, label: 'Transfer', Icon: ArrowRightLeft, iconColor: '#059669', iconBg: '#d1fae5' },
                    ]).map(({ key, label, Icon, iconColor, iconBg }) => {
                      const active = wizardYon === key;
                      return (
                        <div
                          key={key}
                          onClick={() => { setWizardYon(key); setWizardTur(null); }}
                          style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            padding: '16px 12px', borderRadius: 12, cursor: 'pointer', transition: 'all 0.15s',
                            border: active ? '2px solid #cd6906' : '2px solid #f3f4f6',
                            background: active ? 'rgba(205,105,6,0.05)' : '#fff',
                          }}
                          onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; } }}
                          onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = '#f3f4f6'; e.currentTarget.style.background = '#fff'; } }}
                        >
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                            <Icon size={20} color={iconColor} />
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Step 2 — İşlem Türü (sadece gelir/gider için) */}
                {wizardYon && wizardYon !== 'transfer' && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Adım 2 — İşlem Türü</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {turOptions[wizardYon].map(opt => {
                        const active = wizardTur === opt.key;
                        return (
                          <div
                            key={opt.key}
                            onClick={() => setWizardTur(opt.key)}
                            style={{
                              padding: '12px 16px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                              border: active ? '1.5px solid #cd6906' : '1px solid #e5e7eb',
                              background: active ? 'rgba(205,105,6,0.04)' : '#fff',
                            }}
                            onMouseEnter={e => { if (!active) { (e.currentTarget.style.borderColor = 'rgba(205,105,6,0.4)'); e.currentTarget.style.background = '#f9fafb'; } }}
                            onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; } }}
                          >
                            <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{opt.label}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{opt.desc}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Transfer seçildiğinde bilgi mesajı */}
                {wizardYon === 'transfer' && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <ArrowRightLeft size={18} color="#059669" />
                    <div style={{ fontSize: 13, color: '#166534' }}>İki kasa arasında para transferi gerçekleştireceksiniz. Devam Et'e tıklayarak kaynak ve hedef kasayı belirleyin.</div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '20px 28px', borderTop: '1px solid #e5e7eb', background: '#fff',
                display: 'flex', justifyContent: 'flex-end', gap: 10,
                borderRadius: '0 0 16px 16px',
              }}>
                <button
                  onClick={() => setShowIslemSecimModal(false)}
                  style={{
                    padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db',
                    background: '#fff', fontSize: 13, fontWeight: 500, color: '#374151', cursor: 'pointer',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    if (wizardYon === 'transfer') { handleOpenTransferModal(); return; }
                    if (wizardTur) handleIslemSecim(wizardTur);
                  }}
                  disabled={wizardYon !== 'transfer' && !wizardTur}
                  style={{
                    padding: '9px 20px', borderRadius: 8, border: 'none',
                    background: (wizardYon === 'transfer' || wizardTur) ? '#cd6906' : '#e5e7eb',
                    color: (wizardYon === 'transfer' || wizardTur) ? '#fff' : '#9ca3af',
                    fontSize: 13, fontWeight: 600, cursor: (wizardYon === 'transfer' || wizardTur) ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', gap: 6,
                    boxShadow: (wizardYon === 'transfer' || wizardTur) ? '0 1px 3px rgba(205,105,6,0.3)' : 'none',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (wizardYon === 'transfer' || wizardTur) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; } }}
                  onMouseLeave={e => { if (wizardYon === 'transfer' || wizardTur) { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; } }}
                >
                  Devam Et <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Transfer Modal */}
      {showTransferModal && (() => {
        const kaynakBakiye = transferFormData.kaynakKasaID ? getKasaBakiye(parseInt(transferFormData.kaynakKasaID)) : null;
        const hedefBakiye = transferFormData.hedefKasaID ? getKasaBakiye(parseInt(transferFormData.hedefKasaID)) : null;
        const kaynakKasa = kasalar.find(k => k.kasaID === parseInt(transferFormData.kaynakKasaID));
        const hedefKasa = kasalar.find(k => k.kasaID === parseInt(transferFormData.hedefKasaID));
        return (
          <div onClick={() => setShowTransferModal(false)} style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowRightLeft size={18} color="#059669" />
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Kasa Transferi</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>İki kasa arasında para transferi</div>
                  </div>
                </div>
                <button onClick={() => setShowTransferModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, borderRadius: 8 }} onMouseEnter={e => (e.currentTarget.style.color = '#4b5563')} onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}><X size={18} /></button>
              </div>

              {/* Body */}
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* Kaynak Kasa */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Kaynak Kasa <span style={{ color: '#dc2626' }}>*</span></label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={transferFormData.kaynakKasaID}
                      onChange={e => setTransferFormData(prev => ({ ...prev, kaynakKasaID: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, color: '#111827', background: '#fff', appearance: 'none', cursor: 'pointer' }}
                    >
                      <option value="">Kasa seçin...</option>
                      {kasalar.filter(k => k.kasaTuru === 'Nakit').map(k => (
                        <option key={k.kasaID} value={k.kasaID} disabled={String(k.kasaID) === transferFormData.hedefKasaID}>
                          {k.kasaAdi}
                        </option>
                      ))}
                    </select>
                  </div>
                  {kaynakBakiye !== null && kaynakKasa && (
                    <div style={{ marginTop: 5, fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ fontWeight: 600, color: kaynakBakiye >= 0 ? '#059669' : '#dc2626' }}>
                        Mevcut bakiye: {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(kaynakBakiye)} ₺
                      </span>
                    </div>
                  )}
                </div>

                {/* Ok işareti */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#d1fae5', border: '2px solid #6ee7b7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                  </div>
                </div>

                {/* Hedef Kasa */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Hedef Kasa <span style={{ color: '#dc2626' }}>*</span></label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={transferFormData.hedefKasaID}
                      onChange={e => setTransferFormData(prev => ({ ...prev, hedefKasaID: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, color: '#111827', background: '#fff', appearance: 'none', cursor: 'pointer' }}
                    >
                      <option value="">Kasa seçin...</option>
                      {kasalar.filter(k => k.kasaTuru === 'Nakit').map(k => (
                        <option key={k.kasaID} value={k.kasaID} disabled={String(k.kasaID) === transferFormData.kaynakKasaID}>
                          {k.kasaAdi}
                        </option>
                      ))}
                    </select>
                  </div>
                  {hedefBakiye !== null && hedefKasa && (
                    <div style={{ marginTop: 5, fontSize: 12, color: '#6b7280' }}>
                      <span style={{ fontWeight: 600, color: hedefBakiye >= 0 ? '#059669' : '#dc2626' }}>
                        Mevcut bakiye: {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(hedefBakiye)} ₺
                      </span>
                    </div>
                  )}
                </div>

                {/* Miktar + Tarih */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Miktar (₺) <span style={{ color: '#dc2626' }}>*</span></label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={transferFormData.miktar}
                        onChange={e => setTransferFormData(prev => ({ ...prev, miktar: formatMoneyInput(e.target.value) }))}
                        placeholder="0"
                        style={{ width: '100%', padding: '9px 32px 9px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, color: '#111827', boxSizing: 'border-box' }}
                      />
                      <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#9ca3af', pointerEvents: 'none' }}>₺</span>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Tarih <span style={{ color: '#dc2626' }}>*</span></label>
                    <TrDateInput
                      value={transferFormData.tarih}
                      onChange={e => setTransferFormData(prev => ({ ...prev, tarih: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, color: '#111827', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                {/* Açıklama */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Açıklama</label>
                  <input
                    type="text"
                    value={transferFormData.aciklama}
                    onChange={e => setTransferFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                    placeholder="Transfer açıklaması..."
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, color: '#111827', boxSizing: 'border-box' }}
                  />
                  {(transferFormData.kaynakKasaID && transferFormData.hedefKasaID) && (
                    <div style={{ marginTop: 5, fontSize: 11, color: '#9ca3af' }}>
                      Açıklama ön eki: <em>"{kaynakKasa?.kasaAdi || '...'} &gt; {hedefKasa?.kasaAdi || '...'} arası transfer{transferFormData.aciklama ? ' - ' + transferFormData.aciklama : ''}"</em>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button onClick={() => setShowTransferModal(false)} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, fontWeight: 500, color: '#374151', cursor: 'pointer' }} onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')} onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>İptal</button>
                <button
                  onClick={handleTransferSave}
                  disabled={transferSaving}
                  style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontSize: 13, fontWeight: 600, cursor: transferSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: transferSaving ? 0.7 : 1 }}
                >
                  {transferSaving ? <><span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }}></span> Kaydediliyor...</> : <><Check size={14} /> Kaydet</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Yeni İşlem Modal */}
      {showIslemModal && (
        <div className="od-modal-overlay" onClick={() => setShowIslemModal(false)}>
          <div className="od-modal" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
            <div className="od-modal-header" style={{ padding: '14px 20px' }}>
              <div className="od-modal-title" style={{ fontSize: 15 }}>
                {islemModu === 'gelir' ? 'Gelir İşlemi' : islemModu === 'gider' ? 'Gider İşlemi' : islemModu === 'tahsilat' ? 'Tahsilat İşlemi' : islemModu === 'odeme' ? 'Ödeme İşlemi' : 'Yeni İşlem'}
              </div>
              <button className="od-modal-close" onClick={() => setShowIslemModal(false)}>✕</button>
            </div>
            <form onSubmit={handleIslemFormSubmit}>
              <div className="od-modal-body" style={{ padding: '16px 20px' }}>

                {/* ===== GELİR İŞLEMİ — YENİ FORM ===== */}
                {islemModu === 'gelir' && (
                  <>
                    {/* 1. Tarih + Açıklama */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Tarih <span style={{ color: '#dc2626' }}>*</span></div>
                        <input type="text" className="od-input" style={{ width: '100%' }} placeholder="gg.aa.yyyy" value={formTarihDisplay}
                          onChange={e => { const d = e.target.value.replace(/\D/g, '').slice(0, 8); let f = d; if (d.length > 2) f = d.slice(0, 2) + '.' + d.slice(2); if (d.length > 4) f = d.slice(0, 2) + '.' + d.slice(2, 4) + '.' + d.slice(4); setFormTarihDisplay(f); if (d.length === 8) setIslemFormData(prev => ({ ...prev, tarih: `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}` })); }} required />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Açıklama</div>
                        <input type="text" className="od-input" style={{ width: '100%' }} value={islemFormData.aciklama}
                          onChange={(e) => setIslemFormData({ ...islemFormData, aciklama: e.target.value })}
                          placeholder="İşlemle ilgili notlar..." />
                      </div>
                    </div>

                    {/* 2. Tahsilat Biçimi */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 6 }}>Tahsilat Biçimi <span style={{ color: '#dc2626' }}>*</span></div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button"
                          onClick={() => { setTahsilatBicimi('vadeli'); setIslemFormData(prev => ({ ...prev, kasaID: 0, kasaTuru: 'Nakit' })); setCariArama(''); setCariSozlesmeleri([]); }}
                          style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `2px solid ${tahsilatBicimi === 'vadeli' ? '#2563eb' : '#e2e8f0'}`, background: tahsilatBicimi === 'vadeli' ? '#eff6ff' : '#fff', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', textAlign: 'left' as const }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600, color: tahsilatBicimi === 'vadeli' ? '#1d4ed8' : '#374151' }}>Vadeli</div>
                          <div style={{ fontSize: 11, color: tahsilatBicimi === 'vadeli' ? '#3b82f6' : '#9ca3af', marginTop: 2 }}>Müşteri cari hesabına alacak kaydeder</div>
                        </button>
                        <button type="button"
                          onClick={() => { setTahsilatBicimi('vadesiz'); setIslemFormData(prev => ({ ...prev, cariID: '', sozlesmeID: '', kasaID: 0 })); setCariArama(''); setCariSozlesmeleri([]); }}
                          style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `2px solid ${tahsilatBicimi === 'vadesiz' ? '#16a34a' : '#e2e8f0'}`, background: tahsilatBicimi === 'vadesiz' ? '#f0fdf4' : '#fff', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', textAlign: 'left' as const }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600, color: tahsilatBicimi === 'vadesiz' ? '#15803d' : '#374151' }}>Vadesiz</div>
                          <div style={{ fontSize: 11, color: tahsilatBicimi === 'vadesiz' ? '#16a34a' : '#9ca3af', marginTop: 2 }}>Kasaya / banka hesabına doğrudan kaydeder</div>
                        </button>
                      </div>
                    </div>

                    {/* 3a. Vadeli: Cari Tipi + Müşteri/Tedarikçi */}
                    {tahsilatBicimi === 'vadeli' && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 10 }}>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Cari Tipi <span style={{ color: '#dc2626' }}>*</span></div>
                            <select className="od-modal-select" value={cariTipi} onChange={(e) => handleCariTipiChange(e.target.value as 'musteri' | 'tedarikci')}>
                              <option value="musteri">Müşteri</option>
                              <option value="tedarikci">Tedarikçi</option>
                            </select>
                          </div>
                          <div style={{ position: 'relative' }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>
                              {cariTipi === 'musteri' ? 'Müşteri' : 'Tedarikçi'} <span style={{ color: '#dc2626' }}>*</span>
                            </div>
                            {islemFormData.cariID ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '8px 12px' }}>
                                <i className="fas fa-user-check" style={{ color: '#16a34a', fontSize: 12 }}></i>
                                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                                  {cariler.find(c => c.cariID === parseInt(islemFormData.cariID))?.adiSoyadi ?? ''}
                                </span>
                                <button type="button" onClick={() => { setIslemFormData(prev => ({ ...prev, cariID: '', sozlesmeID: '' })); setCariSozlesmeleri([]); }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 13, padding: 0, lineHeight: 1 }}>✕</button>
                              </div>
                            ) : (
                              <input type="text" className="od-input" style={{ width: '100%' }} placeholder="Ara... (en az 3 karakter)"
                                value={cariArama}
                                onChange={(e) => { setCariArama(e.target.value); setShowCariDropdown(true); }}
                                onFocus={() => setShowCariDropdown(true)}
                                onBlur={() => setTimeout(() => setShowCariDropdown(false), 150)}
                                autoComplete="off" />
                            )}
                            {showCariDropdown && !islemFormData.cariID && cariArama.length >= 3 && (() => {
                              const sonuclar = getCariAramaSonuclari();
                              return (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(15,23,42,0.12)', marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                                  {sonuclar.length > 0 ? sonuclar.map(cari => (
                                    <div key={cari.cariID} onMouseDown={() => handleCariSec(cari)}
                                      style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' }}
                                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                      onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                                      <span style={{ fontWeight: 600, color: '#0f172a' }}>{cari.adiSoyadi}</span>
                                      {cari.firmaAdi && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>{cari.firmaAdi}</span>}
                                    </div>
                                  )) : (
                                    <div style={{ padding: '10px 14px', fontSize: 12.5, color: '#94a3b8', textAlign: 'center' }}>Sonuç bulunamadı</div>
                                  )}
                                </div>
                              );
                            })()}
                            {!islemFormData.cariID && cariArama.length > 0 && cariArama.length < 3 && (
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>En az 3 karakter girin</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 3b. Vadesiz: Kasa / Banka Hesabı */}
                    {tahsilatBicimi === 'vadesiz' && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Kasa / Banka Hesabı <span style={{ color: '#dc2626' }}>*</span></div>
                        <select className="od-modal-select" value={islemFormData.kasaID}
                          onChange={(e) => {
                            const kasaId = parseInt(e.target.value);
                            const kasa = kasalar.find((k: any) => k.kasaID === kasaId);
                            setIslemFormData({ ...islemFormData, kasaID: kasaId, kasaTuru: kasa?.kasaTuru || 'Nakit' });
                          }}
                          required
                        >
                          <option value={0}>Hesap Seçin</option>
                          <optgroup label="Nakit Kasalar">
                            {kasalar.filter((k: any) => k.kasaTuru === 'Nakit').map((kasa: any) => (
                              <option key={kasa.kasaID} value={kasa.kasaID}>{kasa.kasaAdi}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Banka Hesapları">
                            {kasalar.filter((k: any) => k.kasaTuru === 'Banka').map((kasa: any) => (
                              <option key={kasa.kasaID} value={kasa.kasaID}>{kasa.kasaAdi}</option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                    )}

                    {/* 4. Gelir Tipi (Kategori) */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Gelir Tipi <span style={{ color: '#94a3b8', fontSize: 9 }}>(Opsiyonel)</span></div>
                      <select className="od-modal-select" value={islemFormData.kategoriID}
                        onChange={(e) => setIslemFormData({ ...islemFormData, kategoriID: e.target.value })}
                      >
                        <option value="">Kategori Seçin</option>
                        {kategoriler.filter((k) => k.tur === 'Gelir').map((k) => (
                          <option key={k.kategoriID} value={k.kategoriID}>{k.kategoriAdi}</option>
                        ))}
                      </select>
                    </div>

                    {/* 5. Tutar */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Tutar <span style={{ color: '#dc2626' }}>*</span></div>
                      <input type="text" className="od-input" style={{ width: '100%' }} value={islemFormData.miktar}
                        onChange={(e) => setIslemFormData({ ...islemFormData, miktar: formatMoneyInput(e.target.value) })}
                        placeholder="0" required />
                    </div>

                    {/* 6. Proje */}
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Proje <span style={{ color: '#94a3b8', fontSize: 9 }}>(Opsiyonel)</span></div>
                      <select className="od-modal-select" value={islemFormData.projeID}
                        onChange={(e) => setIslemFormData({ ...islemFormData, projeID: e.target.value })}
                      >
                        <option value="">Proje Seçin</option>
                        {projeler.map((proje) => (
                          <option key={proje.projeID} value={proje.projeID}>{proje.projeAdi}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* ===== GİDER İŞLEMİ — YENİ FORM ===== */}
                {islemModu === 'gider' && (
                  <>
                    {/* 1. Tarih + Açıklama */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Tarih <span style={{ color: '#dc2626' }}>*</span></div>
                        <input type="text" className="od-input" style={{ width: '100%' }} placeholder="gg.aa.yyyy" value={formTarihDisplay}
                          onChange={e => { const d = e.target.value.replace(/\D/g, '').slice(0, 8); let f = d; if (d.length > 2) f = d.slice(0, 2) + '.' + d.slice(2); if (d.length > 4) f = d.slice(0, 2) + '.' + d.slice(2, 4) + '.' + d.slice(4); setFormTarihDisplay(f); if (d.length === 8) setIslemFormData(prev => ({ ...prev, tarih: `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}` })); }} required />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Açıklama</div>
                        <input type="text" className="od-input" style={{ width: '100%' }} value={islemFormData.aciklama}
                          onChange={(e) => setIslemFormData({ ...islemFormData, aciklama: e.target.value })}
                          placeholder="İşlemle ilgili notlar..." />
                      </div>
                    </div>

                    {/* 2. Ödeme Biçimi toggle */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 6 }}>Ödeme Biçimi <span style={{ color: '#dc2626' }}>*</span></div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button"
                          onClick={() => { setTahsilatBicimi('vadeli'); setIslemFormData(prev => ({ ...prev, kasaID: 0, kasaTuru: 'Nakit' })); setCariArama(''); setCariSozlesmeleri([]); }}
                          style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `2px solid ${tahsilatBicimi === 'vadeli' ? '#dc2626' : '#e2e8f0'}`, background: tahsilatBicimi === 'vadeli' ? '#fef2f2' : '#fff', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', textAlign: 'left' as const }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600, color: tahsilatBicimi === 'vadeli' ? '#dc2626' : '#374151' }}>Vadeli</div>
                          <div style={{ fontSize: 11, color: tahsilatBicimi === 'vadeli' ? '#ef4444' : '#9ca3af', marginTop: 2 }}>Cari hesaba borç kaydeder</div>
                        </button>
                        <button type="button"
                          onClick={() => { setTahsilatBicimi('vadesiz'); setIslemFormData(prev => ({ ...prev, cariID: '', sozlesmeID: '', kasaID: 0 })); setCariArama(''); setCariSozlesmeleri([]); }}
                          style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `2px solid ${tahsilatBicimi === 'vadesiz' ? '#ea580c' : '#e2e8f0'}`, background: tahsilatBicimi === 'vadesiz' ? '#fff7ed' : '#fff', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', textAlign: 'left' as const }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600, color: tahsilatBicimi === 'vadesiz' ? '#ea580c' : '#374151' }}>Vadesiz</div>
                          <div style={{ fontSize: 11, color: tahsilatBicimi === 'vadesiz' ? '#f97316' : '#9ca3af', marginTop: 2 }}>Kasadan / banka hesabından doğrudan çıkarır</div>
                        </button>
                      </div>
                    </div>

                    {/* 3a. Vadeli: Cari Tipi + Tedarikçi/Müşteri */}
                    {tahsilatBicimi === 'vadeli' && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 10 }}>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Cari Tipi <span style={{ color: '#dc2626' }}>*</span></div>
                            <select className="od-modal-select" value={cariTipi} onChange={(e) => handleCariTipiChange(e.target.value as 'musteri' | 'tedarikci')}>
                              <option value="musteri">Müşteri</option>
                              <option value="tedarikci">Tedarikçi</option>
                            </select>
                          </div>
                          <div style={{ position: 'relative' }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>
                              {cariTipi === 'musteri' ? 'Müşteri' : 'Tedarikçi'} <span style={{ color: '#dc2626' }}>*</span>
                            </div>
                            {islemFormData.cariID ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '8px 12px' }}>
                                <i className="fas fa-user-check" style={{ color: '#dc2626', fontSize: 12 }}></i>
                                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                                  {cariler.find(c => c.cariID === parseInt(islemFormData.cariID))?.adiSoyadi ?? ''}
                                </span>
                                <button type="button" onClick={() => { setIslemFormData(prev => ({ ...prev, cariID: '', sozlesmeID: '' })); setCariSozlesmeleri([]); }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 13, padding: 0, lineHeight: 1 }}>✕</button>
                              </div>
                            ) : (
                              <input type="text" className="od-input" style={{ width: '100%' }} placeholder="Ara... (en az 3 karakter)"
                                value={cariArama}
                                onChange={(e) => { setCariArama(e.target.value); setShowCariDropdown(true); }}
                                onFocus={() => setShowCariDropdown(true)}
                                onBlur={() => setTimeout(() => setShowCariDropdown(false), 150)}
                                autoComplete="off" />
                            )}
                            {showCariDropdown && !islemFormData.cariID && cariArama.length >= 3 && (() => {
                              const sonuclar = getCariAramaSonuclari();
                              return (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(15,23,42,0.12)', marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                                  {sonuclar.length > 0 ? sonuclar.map(cari => (
                                    <div key={cari.cariID} onMouseDown={() => handleCariSec(cari)}
                                      style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' }}
                                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                      onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                                      <span style={{ fontWeight: 600, color: '#0f172a' }}>{cari.adiSoyadi}</span>
                                      {cari.firmaAdi && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>{cari.firmaAdi}</span>}
                                    </div>
                                  )) : (
                                    <div style={{ padding: '10px 14px', fontSize: 12.5, color: '#94a3b8', textAlign: 'center' }}>Sonuç bulunamadı</div>
                                  )}
                                </div>
                              );
                            })()}
                            {!islemFormData.cariID && cariArama.length > 0 && cariArama.length < 3 && (
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>En az 3 karakter girin</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 3a-2. Vadeli: Vade Tarihi */}
                    {tahsilatBicimi === 'vadeli' && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>
                          Vade Tarihi <span style={{ color: '#dc2626' }}>*</span>
                        </div>
                        <input
                          type="date"
                          className="od-input"
                          style={{ width: '100%' }}
                          value={islemFormData.vadeTarihi || ''}
                          onChange={e => setIslemFormData(prev => ({ ...prev, vadeTarihi: e.target.value }))}
                          required
                        />
                      </div>
                    )}

                    {/* 3b. Vadesiz: Kasa / Banka Hesabı */}
                    {tahsilatBicimi === 'vadesiz' && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Kasa / Banka Hesabı <span style={{ color: '#dc2626' }}>*</span></div>
                        <select className="od-modal-select" value={islemFormData.kasaID}
                          onChange={(e) => {
                            const kasaId = parseInt(e.target.value);
                            const kasa = kasalar.find((k: any) => k.kasaID === kasaId);
                            setIslemFormData({ ...islemFormData, kasaID: kasaId, kasaTuru: kasa?.kasaTuru || 'Nakit' });
                          }}
                          required
                        >
                          <option value={0}>Hesap Seçin</option>
                          <optgroup label="Nakit Kasalar">
                            {kasalar.filter((k: any) => k.kasaTuru === 'Nakit').map((kasa: any) => (
                              <option key={kasa.kasaID} value={kasa.kasaID}>{kasa.kasaAdi}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Banka Hesapları">
                            {kasalar.filter((k: any) => k.kasaTuru === 'Banka').map((kasa: any) => (
                              <option key={kasa.kasaID} value={kasa.kasaID}>{kasa.kasaAdi}</option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                    )}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Gider Tipi <span style={{ color: '#94a3b8', fontSize: 9 }}>(Opsiyonel)</span></div>
                      <select className="od-modal-select" value={islemFormData.kategoriID}
                        onChange={(e) => setIslemFormData({ ...islemFormData, kategoriID: e.target.value })}
                      >
                        <option value="">Kategori Seçin</option>
                        {kategoriler.filter((k) => k.tur === 'Gider').map((k) => (
                          <option key={k.kategoriID} value={k.kategoriID}>{k.kategoriAdi}</option>
                        ))}
                      </select>
                    </div>

                    {/* 5. Tutar */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Tutar <span style={{ color: '#dc2626' }}>*</span></div>
                      <input type="text" className="od-input" style={{ width: '100%' }} value={islemFormData.miktar}
                        onChange={(e) => setIslemFormData({ ...islemFormData, miktar: formatMoneyInput(e.target.value) })}
                        placeholder="0" required />
                    </div>

                    {/* 6. Proje */}
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 4 }}>Proje <span style={{ color: '#94a3b8', fontSize: 9 }}>(Opsiyonel)</span></div>
                      <select className="od-modal-select" value={islemFormData.projeID}
                        onChange={(e) => setIslemFormData({ ...islemFormData, projeID: e.target.value })}
                      >
                        <option value="">Proje Seçin</option>
                        {projeler.map((proje) => (
                          <option key={proje.projeID} value={proje.projeID}>{proje.projeAdi}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

              </div>
              <div className="od-modal-footer" style={{ padding: '10px 20px' }}>
                <button type="button" className="od-modal-btn-cancel" onClick={() => setShowIslemModal(false)}>İptal</button>
                <button type="submit" className="od-modal-btn-save"><i className="fas fa-save" style={{ marginRight: 6 }}></i>Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingOdeme && (
        <div className="od-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="od-modal" style={{ maxWidth: 480, width: '95%' }} onClick={e => e.stopPropagation()}>
            <div className="od-modal-header">
              <div className="od-modal-title">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#cd6906" width="18" height="18" style={{display:'inline',verticalAlign:'middle',marginRight:6}}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                Kaydı Düzenle
              </div>
              <button className="od-modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="od-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Cari + Yön bilgi satırı */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: 9, border: '1px solid #f1f5f9' }}>
                <span style={{ fontWeight: 700, fontSize: 13.5, color: '#1e293b' }}>{editingOdeme.cari?.adiSoyadi}</span>
                <span className={`od-badge ${editingOdeme.odemeYonu === 'Tahsilat' ? 'od-badge-tahsilat' : 'od-badge-odeme'}`} style={{ marginLeft: 'auto' }}>
                  {editingOdeme.odemeYonu === 'Tahsilat' ? '↓ Tahsilat' : '↑ Ödeme'}
                </span>
                <span style={{ fontSize: 11.5, color: editingOdeme.odemeDurumu === 'Odendi' ? '#16a34a' : '#f59e0b', fontWeight: 700, background: editingOdeme.odemeDurumu === 'Odendi' ? '#dcfce7' : '#fef9c3', borderRadius: 6, padding: '2px 8px' }}>
                  {editingOdeme.odemeDurumu}
                </span>
              </div>

              {/* Tutar */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5, display: 'block' }}>Tutar (₺) *</label>
                <input type="text" className="od-input" style={{ width: '100%', fontSize: 16, fontWeight: 700, textAlign: 'right' }}
                  value={editMiktar}
                  onChange={e => setEditMiktar(formatMoneyInput(e.target.value))}
                />
              </div>

              {/* Tarih + Ödeme Türü */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5, display: 'block' }}>İşlem Tarihi *</label>
                  <input type="text" className="od-input" style={{ width: '100%' }} placeholder="gg.aa.yyyy"
                    value={editTarihDisplay}
                    onChange={e => {
                      const d = e.target.value.replace(/\D/g, '').slice(0, 8);
                      let f = d;
                      if (d.length > 2) f = d.slice(0, 2) + '.' + d.slice(2);
                      if (d.length > 4) f = d.slice(0, 2) + '.' + d.slice(2, 4) + '.' + d.slice(4);
                      setEditTarihDisplay(f);
                      if (d.length === 8) setEditTarih(`${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}`);
                      else setEditTarih('');
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5, display: 'block' }}>Ödeme Türü</label>
                  <select className="od-select" style={{ width: '100%' }} value={editOdemeTuru} onChange={e => setEditOdemeTuru(e.target.value)}>
                    <option value="Nakit">Nakit</option>
                    <option value="BankaHavale">Havale/EFT</option>
                    <option value="Cek">Çek</option>
                    <option value="Senet">Senet</option>
                    <option value="Takas">Takas / Barter</option>
                    <option value="Mahsup">Mahsup</option>
                  </select>
                </div>
              </div>

              {/* Kasa */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5, display: 'block' }}>Kasa</label>
                <select className="od-select" style={{ width: '100%' }} value={editKasaID} onChange={e => setEditKasaID(e.target.value)}>
                  <option value="">— Kasa seçin —</option>
                  {kasalar.map(kasa => (
                    <option key={kasa.kasaID} value={kasa.kasaID}>{kasa.kasaAdi} ({kasa.kasaTuru === 'Nakit' ? '💵' : '🏦'})</option>
                  ))}
                </select>
              </div>

              {/* Açıklama */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5, display: 'block' }}>Açıklama</label>
                <textarea className="od-input" style={{ width: '100%', resize: 'vertical', minHeight: 54 }}
                  value={editAciklama}
                  onChange={e => setEditAciklama(e.target.value)}
                  placeholder="İsteğe bağlı..."
                />
              </div>
            </div>
            <div className="od-modal-footer">
              <button className="od-modal-btn-cancel" style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', marginRight: 'auto' }}
                onClick={handleIptalEt}>
                {editingOdeme.odemeYonu === 'Odeme' ? 'Ödemeyi İptal Et' : 'Tahsilatı İptal Et'}
              </button>
              <button className="od-modal-btn-cancel" onClick={() => setShowEditModal(false)}>Kapat</button>
              <button className="od-modal-btn-save" onClick={handleDuzenlemeKaydet} disabled={editSaving}>
                {editSaving ? 'Kaydediliyor...' : <><i className="fas fa-save" style={{ marginRight: 6 }}></i>Kaydet</>}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Tahsilat/Ödeme İşlemi Modal */}
      {showTahsilatOdemeModal && (
        <div className="od-modal-overlay" onClick={() => setShowTahsilatOdemeModal(false)}>
          <div className="od-modal" style={{ maxWidth: 500, width: '95%' }} onClick={e => e.stopPropagation()}>
            <div className="od-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: tahsilatOdemeModu === 'tahsilat' ? '#dcfce7' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`fas ${tahsilatOdemeModu === 'tahsilat' ? 'fa-arrow-down' : 'fa-arrow-up'}`} style={{ color: tahsilatOdemeModu === 'tahsilat' ? '#16a34a' : '#dc2626', fontSize: 14 }}></i>
                </div>
                <div>
                  <div className="od-modal-title">{tahsilatOdemeModu === 'tahsilat' ? 'Tahsilat Makbuzu' : 'Ödeme Makbuzu'}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                    {tahsilatOdemeStep === 1 && 'Adım 1 — Cari Seçimi'}
                    {tahsilatOdemeStep === 2 && `Adım 2 — Makbuz Formu · ${cariler.find(c => c.cariID === tahsilatOdemeSelectedCari)?.adiSoyadi || ''}`}
                  </div>
                </div>
              </div>
              <button className="od-modal-close" onClick={() => setShowTahsilatOdemeModal(false)}>✕</button>
            </div>
            <div className="od-modal-body" style={{ padding: '16px 20px' }}>
              {/* Step 1: Cari Seçimi */}
              {tahsilatOdemeStep === 1 && (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
                    <input type="text" className="od-input" style={{ flex: 1, marginBottom: 0 }}
                      placeholder="Cari ara..."
                      value={tahsilatOdemeCariArama}
                      onChange={e => setTahsilatOdemeCariArama(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setTahsilatTedarikciGoster(v => !v)}
                      title={tahsilatOdemeModu === 'tahsilat'
                        ? (tahsilatTedarikciGoster ? 'Tedarikçileri gizle' : 'Tedarikçileri göster')
                        : (tahsilatTedarikciGoster ? 'Müşterileri gizle' : 'Müşterileri göster')}
                      style={{ flexShrink: 0, padding: '8px 12px', border: '1.5px solid', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                        background: tahsilatTedarikciGoster ? '#fef3c7' : '#f8fafc',
                        borderColor: tahsilatTedarikciGoster ? '#fde68a' : '#e2e8f0',
                        color: tahsilatTedarikciGoster ? '#92400e' : '#64748b' }}
                    >
                      <i className={`fas ${tahsilatOdemeModu === 'tahsilat' ? 'fa-truck' : 'fa-user'}`} style={{ marginRight: 5 }}></i>
                      {tahsilatOdemeModu === 'tahsilat'
                        ? (tahsilatTedarikciGoster ? 'Tedarikçileri Gizle' : 'Tedarikçileri Göster')
                        : (tahsilatTedarikciGoster ? 'Müşterileri Gizle' : 'Müşterileri Göster')}
                    </button>
                  </div>
                  <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                    {getTahsilatOdemeBekleyenCariler().length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: 13 }}>
                        <i className="fas fa-search" style={{ fontSize: 28, display: 'block', marginBottom: 8 }}></i>
                        Cari bulunamadı.
                      </div>
                    ) : getTahsilatOdemeBekleyenCariler().map(cari => (
                      <div key={cari.cariID}
                        onClick={async () => {
                          setTahsilatOdemeSelectedCari(cari.cariID);
                          setTahsilatOdemeStep(2);
                          fetchCariSozlesmeleri(cari.cariID);
                          setSecilenPlanTaksit(null);
                          setSozlesmePlanTaksitleri([]);
                          if (tahsilatOdemeModu === 'tahsilat') {
                            try {
                              const r = await axiosInstance.get(`/api/OdemePlanTaksitleri?cariID=${cari.cariID}&sadeceBekleyenler=true`);
                              setSozlesmePlanTaksitleri(r.data || []);
                            } catch { setSozlesmePlanTaksitleri([]); }
                          }
                        }}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', borderRadius: 10, marginBottom: 6, border: '1.5px solid #f1f5f9', cursor: 'pointer', background: '#fff', transition: 'all 0.15s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; (e.currentTarget as HTMLDivElement).style.borderColor = '#e2e8f0'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = '#fff'; (e.currentTarget as HTMLDivElement).style.borderColor = '#f1f5f9'; }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13.5, color: '#1e293b' }}>{cari.adiSoyadi}</div>
                          {cari.firmaAdi && <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>{cari.firmaAdi}</div>}
                        </div>
                        {cari.ekstreBakiye !== undefined && cari.ekstreBakiye !== 0 ? (
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 700, fontSize: 12, color: cari.ekstreBakiye > 0 ? '#dc2626' : '#16a34a' }}>{Math.abs(cari.ekstreBakiye).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₺</div>
                            <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{cari.ekstreBakiye > 0 ? 'Borç Bakiyesi' : 'Alacak Bakiyesi'}</div>
                          </div>
                        ) : cari.ekstreBakiye === 0 ? (
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>Dengede</div>
                          </div>
                        ) : cari.bekleyenAdet > 0 ? (
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 700, fontSize: 12, color: tahsilatOdemeModu === 'tahsilat' ? '#16a34a' : '#dc2626' }}>{cari.bekleyenToplam.toLocaleString('tr-TR')} ₺</div>
                            <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{cari.bekleyenAdet} açık</div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Step 2: Bağımsız Makbuz Formu */}
              {tahsilatOdemeStep === 2 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 9, border: '1px solid #f1f5f9', fontSize: 12.5, color: '#475569' }}>
                    <strong>{cariler.find(c => c.cariID === tahsilatOdemeSelectedCari)?.adiSoyadi}</strong> için bağımsız {tahsilatOdemeModu === 'tahsilat' ? 'tahsilat' : 'ödeme'} makbuzu oluşturun.
                    Kasa anında güncellenir; ardından açık faturalara eşleştirebilirsiniz.
                  </div>
                  {tahsilatOdemeModu === 'tahsilat' && sozlesmePlanTaksitleri.length > 0 && (
                    <div style={{ background: '#eff6ff', border: `1.5px solid ${secilenPlanTaksit ? '#3b82f6' : '#bfdbfe'}`, borderRadius: 10, overflow: 'hidden' }}>
                      {/* Accordion Başlık */}
                      <div
                        onClick={() => setTaksitListAcik(prev => !prev)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', cursor: 'pointer', userSelect: 'none' }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          <i className="fas fa-calendar-alt" style={{ marginRight: 6 }}></i>
                          Sözleşme Ödeme Planı
                          <span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: 20, padding: '1px 8px', fontSize: 11, marginLeft: 8, fontWeight: 700 }}>{sozlesmePlanTaksitleri.length} taksit</span>
                          {secilenPlanTaksit && (
                            <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: 20, padding: '1px 8px', fontSize: 11, marginLeft: 6, fontWeight: 700 }}>
                              <i className="fas fa-check" style={{ marginRight: 4 }}></i>Seçildi
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {secilenPlanTaksit && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>{secilenPlanTaksit.miktar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                          )}
                          {secilenPlanTaksit && (
                            <span
                              onClick={e => { e.stopPropagation(); setSecilenPlanTaksit(null); setMakbuzFormData(prev => ({ ...prev, miktar: '', aciklama: '' })); }}
                              style={{ fontSize: 11, color: '#dc2626', cursor: 'pointer', padding: '2px 6px', borderRadius: 4, background: '#fef2f2', border: '1px solid #fecaca' }}
                            >✕ İptal</span>
                          )}
                          <i className={`fas fa-chevron-${taksitListAcik ? 'up' : 'down'}`} style={{ color: '#93c5fd', fontSize: 12 }}></i>
                        </div>
                      </div>
                      {/* Accordion İçerik */}
                      {taksitListAcik && (
                        <div style={{ borderTop: '1px solid #bfdbfe', padding: '8px 10px', maxHeight: 240, overflowY: 'auto' }}>
                          {sozlesmePlanTaksitleri.map(t => {
                            const isSelected = secilenPlanTaksit?.taksitID === t.taksitID;
                            const vadeStr = new Date(t.vadeTarihi).toLocaleDateString('tr-TR');
                            return (
                              <div key={t.taksitID}
                                onClick={() => {
                                  if (isSelected) {
                                    setSecilenPlanTaksit(null);
                                    setMakbuzFormData(prev => ({ ...prev, miktar: '', aciklama: '' }));
                                  } else {
                                    setSecilenPlanTaksit(t);
                                    setMakbuzFormData(prev => ({
                                      ...prev,
                                      miktar: t.miktar.toLocaleString('tr-TR'),
                                      aciklama: `${t.sozlesmeTakipNo ? t.sozlesmeTakipNo + ' - ' : ''}${t.odemeTuruDetay} (Vade: ${vadeStr})`,
                                      sozlesmeID: String(t.sozlesmeID)
                                    }));
                                    setTaksitListAcik(false);
                                  }
                                }}
                                style={{
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '7px 10px', borderRadius: 7, marginBottom: 5, cursor: 'pointer',
                                  background: isSelected ? '#dbeafe' : '#fff',
                                  border: `1.5px solid ${isSelected ? '#3b82f6' : '#e0eaff'}`,
                                  transition: 'all 0.15s'
                                }}
                              >
                                <div>
                                  <span style={{ fontWeight: 600, fontSize: 12.5, color: '#1e3a8a' }}>{t.odemeTuruDetay}</span>
                                  {t.sozlesmeTakipNo && <span style={{ fontSize: 11, color: '#64748b', marginLeft: 6 }}>{t.sozlesmeTakipNo}</span>}
                                  <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>Vade: {vadeStr}</span>
                                </div>
                                <div style={{ fontWeight: 700, color: isSelected ? '#1d4ed8' : '#0f172a', fontSize: 13 }}>
                                  {t.miktar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                  {isSelected && <i className="fas fa-check-circle" style={{ marginLeft: 6, color: '#2563eb' }}></i>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6, display: 'block' }}>Tutar (₺) *</label>
                      <input type="text" className="od-input" style={{ width: '100%', fontSize: 16, fontWeight: 700, textAlign: 'right' }}
                        placeholder="0" value={makbuzFormData.miktar}
                        onChange={e => setMakbuzFormData(prev => ({ ...prev, miktar: formatMoneyInput(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6, display: 'block' }}>Tarih *</label>
                      <input type="text" className="od-input" style={{ width: '100%' }} placeholder="gg.aa.yyyy"
                        value={makbuzTarihDisplay}
                        onChange={e => { const d = e.target.value.replace(/\D/g, '').slice(0, 8); let f = d; if (d.length > 2) f = d.slice(0, 2) + '.' + d.slice(2); if (d.length > 4) f = d.slice(0, 2) + '.' + d.slice(2, 4) + '.' + d.slice(4); setMakbuzTarihDisplay(f); if (d.length === 8) setMakbuzFormData(prev => ({ ...prev, tarih: `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}` })); else setMakbuzFormData(prev => ({ ...prev, tarih: '' })); }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6, display: 'block' }}>Ödeme Türü</label>
                      <select className="od-select" style={{ width: '100%' }}
                        value={makbuzFormData.odemeTuru}
                        onChange={e => setMakbuzFormData(prev => ({ ...prev, odemeTuru: e.target.value }))}
                      >
                        <option value="Nakit">Nakit</option>
                        <option value="BankaHavale">Havale/EFT</option>
                        <option value="Cek">Çek</option>
                        <option value="Senet">Senet</option>
                        <option value="Takas">Takas / Barter</option>
                        <option value="Mahsup">Mahsup (Daire Karşılığı)</option>
                      </select>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6, display: 'block' }}>Kasa *</label>
                      <select className="od-select" style={{ width: '100%' }}
                        value={makbuzFormData.kasaID}
                        onChange={e => setMakbuzFormData(prev => ({ ...prev, kasaID: e.target.value }))}
                      >
                        <option value="">Kasa seçin...</option>
                        {kasalar.map(k => (
                          <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi} ({k.kasaTuru === 'Nakit' ? '💵' : '🏦'})</option>
                        ))}
                      </select>
                    </div>
                    {tahsilatOdemeModu === 'tahsilat' && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6, display: 'block' }}>Sözleşme <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(isteğe bağlı)</span></label>
                        <select className="od-select" style={{ width: '100%' }}
                          value={makbuzFormData.sozlesmeID}
                          onChange={e => setMakbuzFormData(prev => ({ ...prev, sozlesmeID: e.target.value }))}
                        >
                          <option value="">— Sözleşmeye bağlama —</option>
                          {cariSozlesmeleri
                            .filter(s => s.sozlesmeDurumu === 'Sözleşme')
                            .map(s => {
                            const projeAdi = s.daire?.blok?.proje?.projeAdi || '';
                            const blokAdi = s.daire?.blok?.blokAdi || '';
                            const daireNo = s.daire?.daireNo || '';
                            let label = '';
                            if (projeAdi) label += projeAdi;
                            if (blokAdi) label += (label ? ' · ' : '') + blokAdi;
                            if (daireNo) label += (label ? ' · ' : '') + 'Daire ' + daireNo;
                            if (!label) label = s.sozlesmeTakipNo;
                            if (s.sozlesmeFiyati) label += ` · ${s.sozlesmeFiyati.toLocaleString('tr-TR')} ₺`;
                            return (
                              <option key={s.sozlesmeID} value={s.sozlesmeID}>{label}</option>
                            );
                          })}
                        </select>
                      </div>
                    )}
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6, display: 'block' }}>Açıklama</label>
                      <textarea className="od-input" style={{ width: '100%', resize: 'vertical', minHeight: 54 }}
                        placeholder="İsteğe bağlı..."
                        value={makbuzFormData.aciklama}
                        onChange={e => setMakbuzFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              )}

            </div>
            <div className="od-modal-footer">
              {tahsilatOdemeStep === 2 && (
                <button className="od-modal-btn-cancel" onClick={() => setTahsilatOdemeStep(1)}>
                  <i className="fas fa-arrow-left" style={{ marginRight: 6 }}></i>Geri
                </button>
              )}
              <button className="od-modal-btn-cancel" onClick={() => setShowTahsilatOdemeModal(false)}>İptal</button>
              {tahsilatOdemeStep === 2 && (
                <button className="od-modal-btn-save" onClick={handleMakbuzFormSave} disabled={makbuzSaving}>
                  {makbuzSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Odemeler;